import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Highlight, themes } from 'prism-react-renderer';
import { Copy, Sun, Moon, Menu, X } from 'lucide-react';
import { FaInstagram, FaLinkedin, FaFacebook, FaCopy } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import toast, { Toaster } from 'react-hot-toast';
import { getGettingStartedContent } from './categories/GettingStartedCategory';
import { getContactsContent } from './categories/ContactsCategory';
import { getClientPortalsContent } from './categories/ClientPortalsCategory';
import { getInboxContent } from './categories/InboxCategory';
import { getWorkspacesContent } from './categories/WorkspacesCategory';
import { getEmailDesignsContent } from './categories/EmailDesignsCategory';
import { getEmailCampaignsContent } from './categories/EmailCampaignsCategory';
import { getAuthenticationContent } from './categories/AuthenticationCategory';
import { getManageAccountContent } from './categories/ManageAccountCategory';
import { getPipelinesContent } from './categories/PipelinesCategory';
import { getInvoicesContent } from './categories/InvoicesCategory';
import { getBookingPagesContent } from './categories/BookingPagesCategory';
import { getTaskManagementContent } from './categories/TaskManagementCategory';
import { getYourDayContent } from './categories/YourDayCategory';
import { getDashboardContent } from './categories/DashboardCategory';
import { getWhiteboardsContent } from './categories/WhiteboardsCategory';
import { getDatabinsContent } from './categories/DatabinsCategory';
import { getFormsContent } from './categories/FormsCategory';
import { getAutomationsContent } from './categories/AutomationsCategory';

// Available themes:
// themes.vsDark - VS Code Dark (current)
// themes.vsLight - VS Code Light
// themes.dracula - Dracula theme
// themes.duotoneDark - Duotone Dark
// themes.duotoneLight - Duotone Light
// themes.github - GitHub theme
// themes.nightOwl - Night Owl theme
// themes.nightOwlLight - Night Owl Light
// themes.oceanicNext - Oceanic Next
// themes.palenight - Palenight
// themes.shadesOfPurple - Shades of Purple
// themes.ultramin - Ultramin

// Clean theme system using CSS variables
const applyTheme = (isDark) => {
    if (isDark) {
        document.body.classList.remove('light-mode');
    } else {
        document.body.classList.add('light-mode');
    }
};

// API base URL for documentation
const getApiBaseUrl = () => {
    return 'https://ninjapipe.app/api';
};

const rowBase = {
    margin: '2px 8px',
    borderRadius: '8px',
    fontSize: '15px',
    lineHeight: 1.8,
    cursor: 'pointer',
    transition: 'background 120ms ease, color 120ms ease',
    userSelect: 'none',
    WebkitUserSelect: 'none',
};

const DocsPage = () => {
    const { section, page } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [selectedItem, setSelectedItem] = useState(null);
    const [gettingStartedExpanded, setGettingStartedExpanded] = useState(true);
    const [groupExpanded, setGroupExpanded] = useState({});
    const [chapterExpanded, setChapterExpanded] = useState({});
    const [integrationsExpanded, setIntegrationsExpanded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);
    const [showShortcutAnimation, setShowShortcutAnimation] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(() => {
        // Initialize theme from localStorage or default to dark mode
        const savedTheme = localStorage.getItem('ninjapipe-theme');
        if (savedTheme === 'light') return false;
        if (savedTheme === 'dark') return true;
        return true; // Default to dark mode
    });
    const [isToggling, setIsToggling] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const mainContentRef = useRef(null);
    const activeSidebarItemRef = useRef(null);
    const lastScrolledPathRef = useRef(null);

    const closeSidebar = useCallback(() => setSidebarOpen(false), []);

    const toggleTheme = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (isToggling) return; // Prevent double clicks
        
        setIsToggling(true);
        const newDarkMode = !isDarkMode;
        setIsDarkMode(newDarkMode);
        
        // Save theme preference to localStorage
        localStorage.setItem('ninjapipe-theme', newDarkMode ? 'dark' : 'light');
        
        // Apply theme using CSS variables
        applyTheme(newDarkMode);
        
        // Reset toggling state after animation completes
        setTimeout(() => {
            setIsToggling(false);
        }, 300);
    };

    // Initialize theme on mount
    useEffect(() => {
        applyTheme(isDarkMode);
    }, [isDarkMode]);

    // Scroll main content to top when navigating to another doc page
    useEffect(() => {
        const el = mainContentRef.current;
        if (el) el.scrollTo(0, 0);
    }, [location.pathname]);

    // Close sidebar on Escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') setSidebarOpen(false);
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Focus search when "/" is pressed
            if (event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                // Don't trigger if user is typing in an input field
                if (event.target.tagName !== 'INPUT' && event.target.tagName !== 'TEXTAREA') {
                    event.preventDefault();
                    searchInputRef.current?.focus();
                    
                    // Trigger animation
                    setShowShortcutAnimation(true);
                    setTimeout(() => setShowShortcutAnimation(false), 600);
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const gettingStartedItems = [
        'Introduction',
        'Quick Start',
        'Customize the sidebar',
    ];

    // Sidebar: optional group > chapter > items. Nodes are either { type: 'group', label, chapters } or { type: 'chapter', label, items }.
    const docSidebar = [
        {
            type: 'group',
            label: 'Workspace Settings',
            chapters: [
                {
                    label: 'General',
                    items: [
                        'Workspaces Overview',
                        'Workspace Details',
                        'Branding & Appearance',
                        'Client Portal Branding',
                        'Domain',
                        'Copying workspace settings'
                    ]
                },
                {
                    label: 'Users & Access',
                    items: [
                        'Members',
                        'Roles & Permissions',
                        'Single Sign-On (SSO)'
                    ]
                },
                {
                    label: 'Data & Customizations',
                    items: [
                        'Currency & Localization',
                        'Data Stores',
                        'Custom Fields Designer',
                        'Meta Tags',
                        'Customize Views',
                        'Lead Assignment',
                        'Email Signatures',
                        'Data Import/Export',
                        'Your Data',
                        'Workspace Usage',
                        'NinjaPipe API',
                        'MCP (AI Assistants)'
                    ]
                }
            ]
        },
        {
            type: 'group',
            label: 'Contacts',
            chapters: [
                {
                    label: 'Contacts',
                    items: [
                        'Contact Management',
                        'Finding and Viewing Contacts',
                        'Adding a New Contact',
                        'The Contact Detail Page',
                        'Editing a Contact',
                        'Bulk Actions',
                        'Importing Contacts',
                        'Exporting Contacts',
                        'Duplicates and Merging',
                        'Companies and Deals',
                        'Custom Fields',
                        'Lead Assignment',
                        'Archiving Contacts',
                        'Permissions and Access',
                        'Contacts in Lists',
                        'Adding to List',
                        'Assigning a Meta Tag',
                        'Where Contacts Come From',
                        'Ninja Views'
                    ]
                },
                {
                    label: 'Client Portals',
                    items: [
                        'Client Portals Overview',
                        'Enabling and Managing Access',
                        'Sharing Content',
                        'Announcements and the Timeline',
                        'Approval requests',
                        'Branding and Settings',
                        'Client portal emails',
                        'The Client Portal Experience'
                    ]
                }
            ]
        },
        {
            type: 'group',
            label: 'Campaigns',
            chapters: [
                {
                    label: 'Email campaigns',
                    items: [
                        'Email Campaigns Overview',
                        'Email Campaigns list page'
                    ]
                },
                {
                    label: 'Email designs',
                    items: [
                        'Email Designs Overview',
                        'Email Designs list page',
                        'Creating an email design',
                        'Email designer overview',
                        'Email templates gallery'
                    ]
                }
            ]
        },
        {
            type: 'group',
            label: 'Inbox',
            chapters: [
                {
                    label: 'Inbox',
                    items: [
                        'Inbox Overview',
                        'Connecting Email',
                        'Connecting WhatsApp',
                        'Connecting SMS',
                        'Inbox Settings and Inbox Zero',
                        'Channels, Filters, and Search',
                        'Thread List and Reading Messages',
                        'Syncing and Real-Time Updates'
                    ]
                }
            ]
        },
        {
            type: 'group',
            label: 'Accounts & Login',
            chapters: [
                {
                    label: 'Authentication',
                    items: ['Authentication', 'SSO Setup']
                },
                {
                    label: 'Manage your account',
                    items: ['Account details', 'Integrations', 'Security', 'Licenses', 'Billing & Subscription']
                }
            ]
        },
        {
            type: 'group',
            label: 'Pipelines',
            chapters: [
                {
                    label: 'Pipelines overview',
                    items: ['Pipelines Overview', 'Creating a pipeline', 'Pipeline list page']
                },
                {
                    label: 'Viewing and managing a pipeline',
                    items: ['Opening a pipeline', 'Board and stages', 'Adding pipeline items', 'Pipeline item cards', 'Item detail panel', 'Search, filters, and sort']
                },
                {
                    label: 'Pipeline settings',
                    items: ['General settings', 'Stages', 'Card display', 'Pipeline overview (list card)', 'Column order', 'Permissions', 'Danger zone']
                },
                {
                    label: 'Import, export, and bulk actions',
                    items: ['Adding items in bulk', 'Exporting a pipeline', 'Exporting all pipelines', 'Duplicating a pipeline']
                },
                {
                    label: 'Pipeline types and conversion',
                    items: ['Pipeline types (Contacts, Deals, General)', 'Won and Lost stages']
                }
            ]
        },
        {
            type: 'group',
            label: 'Invoices',
            chapters: [
                {
                    label: 'Invoices overview',
                    items: ['Invoices Overview', 'Invoices list page', 'Creating and editing an invoice']
                },
                {
                    label: 'Invoice designer',
                    items: ['Invoice designer overview', 'General, client, and company data', 'Line items and products', 'Header, footer, and table appearance', 'Labels, tax, and fees', 'Signatures and payment options']
                },
                {
                    label: 'Sending, sharing, and viewing',
                    items: ['Sending an invoice by email', 'Public invoice link and view invoice page', 'Recording payments and invoice statuses']
                },
                {
                    label: 'Templates',
                    items: ['Invoice templates']
                }
            ]
        },
        {
            type: 'group',
            label: 'Booking Pages',
            chapters: [
                {
                    label: 'Overview',
                    items: ['Booking Pages Overview', 'Booking pages list', 'Creating a booking page']
                },
                {
                    label: 'Details and bookings',
                    items: ['Booking page details page', 'Managing bookings and booking detail modal']
                },
                {
                    label: 'Settings',
                    items: ['Booking page settings overview', 'Basic info, members, and form', 'Limits, appearance, and after booking', 'Booking promo codes', 'Analytics and optimization insights']
                },
                {
                    label: 'Availability and public page',
                    items: ['Availability and calendar connections', 'Public booking page and booker experience', 'Embedding the booking page']
                }
            ]
        },
        {
            type: 'group',
            label: 'Dashboard',
            chapters: [
                {
                    label: 'Overview',
                    items: ['Dashboard custom metrics']
                }
            ]
        },
        {
            type: 'group',
            label: 'Your Day',
            chapters: [
                {
                    label: 'Overview',
                    items: ['Your Day Overview']
                },
                {
                    label: 'Productivity',
                    items: ['Quick Notes', 'Background jobs', 'Calendar and team calendars']
                }
            ]
        },
        {
            type: 'group',
            label: 'Task Management',
            chapters: [
                {
                    label: 'Overview',
                    items: ['Task Management Overview', 'Projects list page', 'Opening a project and project tasks page']
                },
                {
                    label: 'Projects',
                    items: ['Creating and editing a project', 'Duplicating and deleting a project']
                },
                {
                    label: 'Tasks',
                    items: ['General tasks', 'Tasks inside a project', 'Creating and editing tasks', 'Subtasks and reordering']
                },
                {
                    label: 'Search, filters, and sort',
                    items: ['Search, filters, and sort (tasks)']
                }
            ]
        },
        {
            type: 'group',
            label: 'Whiteboards',
            chapters: [
                {
                    label: 'Overview',
                    items: ['Whiteboards Overview', 'Whiteboards list page']
                },
                {
                    label: 'Create and manage',
                    items: ['Creating a whiteboard']
                },
                {
                    label: 'Canvas and collaboration',
                    items: ['Using the whiteboard canvas', 'Sharing and collaboration']
                }
            ]
        },
        {
            type: 'group',
            label: 'Databins',
            chapters: [
                {
                    label: 'Overview',
                    items: ['Databins Overview', 'Databins list page']
                },
                {
                    label: 'Create and manage',
                    items: ['Creating and editing a databin', 'Databin details and requests', 'Databin webhook signing']
                },
                {
                    label: 'Using databins in NinjaPipe',
                    items: ['How databins connect to other features']
                }
            ]
        },
        {
            type: 'group',
            label: 'Forms',
            chapters: [
                {
                    label: 'Overview',
                    items: ['Forms Overview', 'Forms list page']
                },
                {
                    label: 'Create and design',
                    items: ['Creating and editing a form', 'Form designer overview', 'Form fields and field types', 'Form settings and appearance']
                },
                {
                    label: 'Share and responses',
                    items: ['Sharing and embedding forms', 'Form responses']
                },
                {
                    label: 'Automation',
                    items: ['Forms and workflows']
                }
            ]
        },
        {
            type: 'group',
            label: 'Automations',
            chapters: [
                {
                    label: 'Overview',
                    items: ['Automations Overview', 'Automations list page']
                },
                {
                    label: 'Create and design',
                    items: ['Creating an automation', 'Workflow designer overview']
                },
                {
                    label: 'Triggers, actions, and conditions',
                    items: ['Triggers', 'Actions and steps', 'Conditions and branching']
                },
                {
                    label: 'Variables and execution',
                    items: ['Variables and data', 'Running automations and executions']
                }
            ]
        }
    ];

    const getChapterKey = (groupLabel, chapterLabel) => `${groupLabel ?? ''}|${chapterLabel}`;

    const findGroupAndChapterForPage = (pageName) => {
        for (const node of docSidebar) {
            if (node.type === 'group') {
                for (const chapter of node.chapters) {
                    if (chapter.items.includes(pageName)) return { group: node, chapter };
                }
            } else if (node.type === 'chapter' && node.items.includes(pageName)) {
                return { group: null, chapter: { label: node.label, items: node.items } };
            }
        }
        return null;
    };

    const integrationsItems = [];

    // URL mapping functions
    const getPageFromUrl = (section, page) => {
        if (!section && !page) return 'Introduction'; // Default page
        
        if (section === 'getting-started') {
            if (page === 'introduction') return 'Introduction';
            if (page === 'quick-start') return 'Quick Start';
            if (page === 'customize-the-sidebar') return 'Customize the sidebar';
            return 'Introduction';
        }
        
        if (section === 'features') {
            if (page === 'authentication') return 'Authentication';
            if (page === 'contact-management') return 'Contact Management';
            if (page === 'finding-and-viewing-contacts') return 'Finding and Viewing Contacts';
            if (page === 'adding-a-new-contact') return 'Adding a New Contact';
            if (page === 'contact-detail-page') return 'The Contact Detail Page';
            if (page === 'editing-a-contact') return 'Editing a Contact';
            if (page === 'bulk-actions') return 'Bulk Actions';
            if (page === 'importing-contacts') return 'Importing Contacts';
            if (page === 'exporting-contacts') return 'Exporting Contacts';
            if (page === 'duplicates-and-merging') return 'Duplicates and Merging';
            if (page === 'companies-and-deals') return 'Companies and Deals';
            if (page === 'custom-fields') return 'Custom Fields';
            if (page === 'lead-assignment') return 'Lead Assignment';
            if (page === 'archiving-contacts') return 'Archiving Contacts';
            if (page === 'permissions-and-access') return 'Permissions and Access';
            if (page === 'contacts-in-lists') return 'Contacts in Lists';
            if (page === 'adding-to-list') return 'Adding to List';
            if (page === 'assigning-a-meta-tag') return 'Assigning a Meta Tag';
            if (page === 'where-contacts-come-from') return 'Where Contacts Come From';
            if (page === 'client-portals-overview') return 'Client Portals Overview';
            if (page === 'enabling-and-managing-access') return 'Enabling and Managing Access';
            if (page === 'contact-detail-page-and-client-portal') return 'Sharing Content';
            if (page === 'sharing-content') return 'Sharing Content';
            if (page === 'announcements-and-the-timeline') return 'Announcements and the Timeline';
            if (page === 'approval-requests') return 'Approval requests';
            if (page === 'branding-and-settings') return 'Branding and Settings';
            if (page === 'client-portal-emails') return 'Client portal emails';
            if (page === 'the-client-portal-experience') return 'The Client Portal Experience';
            if (page === 'inbox-overview') return 'Inbox Overview';
            if (page === 'connecting-email') return 'Connecting Email';
            if (page === 'connecting-whatsapp') return 'Connecting WhatsApp';
            if (page === 'connecting-sms') return 'Connecting SMS';
            if (page === 'connecting-email-whatsapp-and-sms') return 'Connecting Email'; // legacy redirect
            if (page === 'inbox-settings-and-inbox-zero') return 'Inbox Settings and Inbox Zero';
            if (page === 'channels-filters-and-search') return 'Channels, Filters, and Search';
            if (page === 'thread-list-and-reading-messages') return 'Thread List and Reading Messages';
            if (page === 'syncing-and-real-time-updates') return 'Syncing and Real-Time Updates';
            if (page === 'workspaces-overview') return 'Workspaces Overview';
            if (page === 'workspace-details') return 'Workspace Details';
            if (page === 'branding-and-appearance') return 'Branding & Appearance';
            if (page === 'client-portal-branding') return 'Client Portal Branding';
            if (page === 'domain') return 'Domain';
            if (page === 'copying-workspace-settings') return 'Copying workspace settings';
            if (page === 'members') return 'Members';
            if (page === 'roles-and-permissions') return 'Roles & Permissions';
            if (page === 'single-sign-on-sso') return 'Single Sign-On (SSO)';
            if (page === 'currency-and-localization') return 'Currency & Localization';
            if (page === 'data-stores') return 'Data Stores';
            if (page === 'custom-fields-designer') return 'Custom Fields Designer';
            if (page === 'meta-tags') return 'Meta Tags';
            if (page === 'customize-views') return 'Customize Views';
            if (page === 'create-ninja-view') return 'Ninja Views';
            if (page === 'lead-assignment') return 'Lead Assignment';
            if (page === 'email-templates') return 'Email Designs Overview'; // Redirect: Email Templates removed from workspace settings
            if (page === 'email-campaigns-overview') return 'Email Campaigns Overview';
            if (page === 'email-campaigns-list-page') return 'Email Campaigns list page';
            if (page === 'email-designs-overview') return 'Email Designs Overview';
            if (page === 'email-designs-list-page') return 'Email Designs list page';
            if (page === 'creating-an-email-design') return 'Creating an email design';
            if (page === 'email-designer-overview') return 'Email designer overview';
            if (page === 'email-templates-gallery') return 'Email templates gallery';
            if (page === 'email-signatures') return 'Email Signatures';
            if (page === 'data-import-export') return 'Data Import/Export';
            if (page === 'your-data') return 'Your Data';
            if (page === 'workspace-usage') return 'Workspace Usage';
            if (page === 'ninjapipe-api') return 'NinjaPipe API';
            if (page === 'mcp-ai-assistants') return 'MCP (AI Assistants)';
            if (page === 'sso-setup') return 'SSO Setup';
            if (page === 'account-details') return 'Account details';
            if (page === 'integrations') return 'Integrations';
            if (page === 'security') return 'Security';
            if (page === 'licenses') return 'Licenses';
            if (page === 'billing-subscription') return 'Billing & Subscription';
            if (page === 'pipelines-overview') return 'Pipelines Overview';
            if (page === 'creating-a-pipeline') return 'Creating a pipeline';
            if (page === 'pipeline-list-page') return 'Pipeline list page';
            if (page === 'opening-a-pipeline') return 'Opening a pipeline';
            if (page === 'board-and-stages') return 'Board and stages';
            if (page === 'adding-pipeline-items') return 'Adding pipeline items';
            if (page === 'pipeline-item-cards') return 'Pipeline item cards';
            if (page === 'item-detail-panel') return 'Item detail panel';
            if (page === 'search-filters-and-sort') return 'Search, filters, and sort';
            if (page === 'pipeline-general-settings') return 'General settings';
            if (page === 'pipeline-stages') return 'Stages';
            if (page === 'pipeline-card-display') return 'Card display';
            if (page === 'pipeline-overview-list-card') return 'Pipeline overview (list card)';
            if (page === 'pipeline-column-order') return 'Column order';
            if (page === 'pipeline-permissions') return 'Permissions';
            if (page === 'pipeline-danger-zone') return 'Danger zone';
            if (page === 'adding-items-in-bulk') return 'Adding items in bulk';
            if (page === 'exporting-a-pipeline') return 'Exporting a pipeline';
            if (page === 'exporting-all-pipelines') return 'Exporting all pipelines';
            if (page === 'duplicating-a-pipeline') return 'Duplicating a pipeline';
            if (page === 'pipeline-types-contacts-deals-general') return 'Pipeline types (Contacts, Deals, General)';
            if (page === 'won-and-lost-stages') return 'Won and Lost stages';
            if (page === 'invoices-overview') return 'Invoices Overview';
            if (page === 'invoices-list-page') return 'Invoices list page';
            if (page === 'creating-and-editing-an-invoice') return 'Creating and editing an invoice';
            if (page === 'invoice-designer-overview') return 'Invoice designer overview';
            if (page === 'invoice-general-client-company-data') return 'General, client, and company data';
            if (page === 'invoice-line-items-and-products') return 'Line items and products';
            if (page === 'invoice-header-footer-table') return 'Header, footer, and table appearance';
            if (page === 'invoice-labels-tax-and-fees') return 'Labels, tax, and fees';
            if (page === 'invoice-signatures-and-payment-options') return 'Signatures and payment options';
            if (page === 'sending-an-invoice-by-email') return 'Sending an invoice by email';
            if (page === 'public-invoice-link-and-view-page') return 'Public invoice link and view invoice page';
            if (page === 'recording-payments-and-invoice-statuses') return 'Recording payments and invoice statuses';
            if (page === 'invoice-templates') return 'Invoice templates';
            if (page === 'booking-pages-overview') return 'Booking Pages Overview';
            if (page === 'booking-pages-list') return 'Booking pages list';
            if (page === 'creating-a-booking-page') return 'Creating a booking page';
            if (page === 'booking-page-details-page') return 'Booking page details page';
            if (page === 'managing-bookings-and-booking-detail-modal') return 'Managing bookings and booking detail modal';
            if (page === 'booking-page-settings-overview') return 'Booking page settings overview';
            if (page === 'basic-info-members-and-form') return 'Basic info, members, and form';
            if (page === 'limits-appearance-and-after-booking') return 'Limits, appearance, and after booking';
            if (page === 'booking-promo-codes') return 'Booking promo codes';
            if (page === 'analytics-and-optimization-insights') return 'Analytics and optimization insights';
            if (page === 'availability-and-calendar-connections') return 'Availability and calendar connections';
            if (page === 'public-booking-page-and-booker-experience') return 'Public booking page and booker experience';
            if (page === 'embedding-the-booking-page') return 'Embedding the booking page';
            if (page === 'your-day-overview') return 'Your Day Overview';
            if (page === 'quick-notes') return 'Quick Notes';
            if (page === 'background-jobs') return 'Background jobs';
            if (page === 'calendar-and-team-calendars') return 'Calendar and team calendars';
            if (page === 'dashboard-custom-metrics') return 'Dashboard custom metrics';
            if (page === 'task-management-overview') return 'Task Management Overview';
            if (page === 'projects-list-page') return 'Projects list page';
            if (page === 'opening-a-project-and-project-tasks-page') return 'Opening a project and project tasks page';
            if (page === 'creating-and-editing-a-project') return 'Creating and editing a project';
            if (page === 'duplicating-and-deleting-a-project') return 'Duplicating and deleting a project';
            if (page === 'general-tasks') return 'General tasks';
            if (page === 'tasks-inside-a-project') return 'Tasks inside a project';
            if (page === 'creating-and-editing-tasks') return 'Creating and editing tasks';
            if (page === 'subtasks-and-reordering') return 'Subtasks and reordering';
            if (page === 'search-filters-and-sort-tasks') return 'Search, filters, and sort (tasks)';
            if (page === 'whiteboards-overview') return 'Whiteboards Overview';
            if (page === 'whiteboards-list-page') return 'Whiteboards list page';
            if (page === 'creating-a-whiteboard') return 'Creating a whiteboard';
            if (page === 'using-the-whiteboard-canvas') return 'Using the whiteboard canvas';
            if (page === 'sharing-and-collaboration') return 'Sharing and collaboration';
            if (page === 'databins-overview') return 'Databins Overview';
            if (page === 'databins-list-page') return 'Databins list page';
            if (page === 'creating-and-editing-a-databin') return 'Creating and editing a databin';
            if (page === 'databin-details-and-requests') return 'Databin details and requests';
            if (page === 'databin-webhook-signing') return 'Databin webhook signing';
            if (page === 'how-databins-connect-to-other-features') return 'How databins connect to other features';
            if (page === 'forms-overview') return 'Forms Overview';
            if (page === 'forms-list-page') return 'Forms list page';
            if (page === 'creating-and-editing-a-form') return 'Creating and editing a form';
            if (page === 'form-designer-overview') return 'Form designer overview';
            if (page === 'form-fields-and-field-types') return 'Form fields and field types';
            if (page === 'form-settings-and-appearance') return 'Form settings and appearance';
            if (page === 'sharing-and-embedding-forms') return 'Sharing and embedding forms';
            if (page === 'form-responses') return 'Form responses';
            if (page === 'forms-and-workflows') return 'Forms and workflows';
            if (page === 'automations-overview') return 'Automations Overview';
            if (page === 'automations-list-page') return 'Automations list page';
            if (page === 'creating-an-automation') return 'Creating an automation';
            if (page === 'workflow-designer-overview') return 'Workflow designer overview';
            if (page === 'triggers') return 'Triggers';
            if (page === 'actions-and-steps') return 'Actions and steps';
            if (page === 'conditions-and-branching') return 'Conditions and branching';
            if (page === 'variables-and-data') return 'Variables and data';
            if (page === 'running-automations-and-executions') return 'Running automations and executions';
            return 'Authentication'; // Default for features section
        }
        
        if (section === 'integrations') {
            return 'Introduction'; // No integrations pages yet
        }
        
        return 'Introduction'; // Fallback
    };

    // Handle URL changes and update selected item
    useEffect(() => {
        // Redirect old support/sso-setup URL to features/sso-setup
        if (section === 'support' && page === 'sso-setup') {
            navigate('/docs/features/sso-setup', { replace: true });
            return;
        }

        const pageFromUrl = getPageFromUrl(section, page);
        setSelectedItem(pageFromUrl);
        
        // Expand the group and chapter that contain this page (when on /docs/features/...)
        if (section === 'features') {
            const found = findGroupAndChapterForPage(pageFromUrl);
            if (found) {
                const { group, chapter } = found;
                const chKey = getChapterKey(group?.label, chapter.label);
                if (group) setGroupExpanded(prev => ({ ...prev, [group.label]: true }));
                setChapterExpanded(prev => ({ ...prev, [chKey]: true }));
            }
        }
        // Redirect to default page if no specific page is selected
        if (!section && !page && location.pathname === '/api-docs') {
            navigate('/api-docs/introduction/authentication', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section, page, location.pathname, navigate]);

    // Scroll sidebar to bring active item into view only once per page (on load/navigation), not when user expands other sections
    useEffect(() => {
        if (!selectedItem) return;
        const pathKey = location.pathname;
        if (lastScrolledPathRef.current === pathKey) return;
        lastScrolledPathRef.current = pathKey;
        const id = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                activeSidebarItemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            });
        });
        return () => cancelAnimationFrame(id);
    }, [selectedItem, location.pathname, groupExpanded, chapterExpanded, gettingStartedExpanded]);

    const getUrlFromPage = useCallback((pageName) => {
        if (pageName === 'Introduction') return '/docs/getting-started/introduction';
        if (pageName === 'Quick Start') return '/docs/getting-started/quick-start';
        if (pageName === 'Customize the sidebar') return '/docs/getting-started/customize-the-sidebar';
        if (pageName === 'Authentication') return '/docs/features/authentication';
        if (pageName === 'Contact Management') return '/docs/features/contact-management';
        if (pageName === 'Finding and Viewing Contacts') return '/docs/features/finding-and-viewing-contacts';
        if (pageName === 'Adding a New Contact') return '/docs/features/adding-a-new-contact';
        if (pageName === 'The Contact Detail Page') return '/docs/features/contact-detail-page';
        if (pageName === 'Editing a Contact') return '/docs/features/editing-a-contact';
        if (pageName === 'Bulk Actions') return '/docs/features/bulk-actions';
        if (pageName === 'Importing Contacts') return '/docs/features/importing-contacts';
        if (pageName === 'Exporting Contacts') return '/docs/features/exporting-contacts';
        if (pageName === 'Duplicates and Merging') return '/docs/features/duplicates-and-merging';
        if (pageName === 'Companies and Deals') return '/docs/features/companies-and-deals';
        if (pageName === 'Custom Fields') return '/docs/features/custom-fields';
        if (pageName === 'Lead Assignment') return '/docs/features/lead-assignment';
        if (pageName === 'Archiving Contacts') return '/docs/features/archiving-contacts';
        if (pageName === 'Permissions and Access') return '/docs/features/permissions-and-access';
        if (pageName === 'Contacts in Lists') return '/docs/features/contacts-in-lists';
        if (pageName === 'Adding to List') return '/docs/features/adding-to-list';
        if (pageName === 'Assigning a Meta Tag') return '/docs/features/assigning-a-meta-tag';
        if (pageName === 'Where Contacts Come From') return '/docs/features/where-contacts-come-from';
        if (pageName === 'Client Portals Overview') return '/docs/features/client-portals-overview';
        if (pageName === 'Enabling and Managing Access') return '/docs/features/enabling-and-managing-access';
        if (pageName === 'Sharing Content') return '/docs/features/sharing-content';
        if (pageName === 'Announcements and the Timeline') return '/docs/features/announcements-and-the-timeline';
        if (pageName === 'Approval requests') return '/docs/features/approval-requests';
        if (pageName === 'Branding and Settings') return '/docs/features/branding-and-settings';
        if (pageName === 'Client portal emails') return '/docs/features/client-portal-emails';
        if (pageName === 'The Client Portal Experience') return '/docs/features/the-client-portal-experience';
        if (pageName === 'Inbox Overview') return '/docs/features/inbox-overview';
        if (pageName === 'Connecting Email') return '/docs/features/connecting-email';
        if (pageName === 'Connecting WhatsApp') return '/docs/features/connecting-whatsapp';
        if (pageName === 'Connecting SMS') return '/docs/features/connecting-sms';
        if (pageName === 'Inbox Settings and Inbox Zero') return '/docs/features/inbox-settings-and-inbox-zero';
        if (pageName === 'Channels, Filters, and Search') return '/docs/features/channels-filters-and-search';
        if (pageName === 'Thread List and Reading Messages') return '/docs/features/thread-list-and-reading-messages';
        if (pageName === 'Syncing and Real-Time Updates') return '/docs/features/syncing-and-real-time-updates';
        if (pageName === 'Workspaces Overview') return '/docs/features/workspaces-overview';
        if (pageName === 'Workspace Details') return '/docs/features/workspace-details';
        if (pageName === 'Branding & Appearance') return '/docs/features/branding-and-appearance';
        if (pageName === 'Client Portal Branding') return '/docs/features/client-portal-branding';
        if (pageName === 'Domain') return '/docs/features/domain';
        if (pageName === 'Copying workspace settings') return '/docs/features/copying-workspace-settings';
        if (pageName === 'Members') return '/docs/features/members';
        if (pageName === 'Roles & Permissions') return '/docs/features/roles-and-permissions';
        if (pageName === 'Single Sign-On (SSO)') return '/docs/features/single-sign-on-sso';
        if (pageName === 'Currency & Localization') return '/docs/features/currency-and-localization';
        if (pageName === 'Data Stores') return '/docs/features/data-stores';
        if (pageName === 'Custom Fields Designer') return '/docs/features/custom-fields-designer';
        if (pageName === 'Meta Tags') return '/docs/features/meta-tags';
        if (pageName === 'Customize Views') return '/docs/features/customize-views';
        if (pageName === 'Ninja Views') return '/docs/features/create-ninja-view';
        if (pageName === 'Lead Assignment') return '/docs/features/lead-assignment';
        if (pageName === 'Email Templates') return '/docs/features/email-designs-overview'; // Redirect: Email Templates → Email Designs
        if (pageName === 'Email Campaigns Overview') return '/docs/features/email-campaigns-overview';
        if (pageName === 'Email Campaigns list page') return '/docs/features/email-campaigns-list-page';
        if (pageName === 'Email Designs Overview') return '/docs/features/email-designs-overview';
        if (pageName === 'Email Designs list page') return '/docs/features/email-designs-list-page';
        if (pageName === 'Creating an email design') return '/docs/features/creating-an-email-design';
        if (pageName === 'Email designer overview') return '/docs/features/email-designer-overview';
        if (pageName === 'Email templates gallery') return '/docs/features/email-templates-gallery';
        if (pageName === 'Email Signatures') return '/docs/features/email-signatures';
        if (pageName === 'Data Import/Export') return '/docs/features/data-import-export';
        if (pageName === 'Your Data') return '/docs/features/your-data';
        if (pageName === 'Workspace Usage') return '/docs/features/workspace-usage';
        if (pageName === 'NinjaPipe API') return '/docs/features/ninjapipe-api';
        if (pageName === 'MCP (AI Assistants)') return '/docs/features/mcp-ai-assistants';
        if (pageName === 'SSO Setup') return '/docs/features/sso-setup';
        if (pageName === 'Account details') return '/docs/features/account-details';
        if (pageName === 'Integrations') return '/docs/features/integrations';
        if (pageName === 'Security') return '/docs/features/security';
        if (pageName === 'Licenses') return '/docs/features/licenses';
        if (pageName === 'Billing & Subscription') return '/docs/features/billing-subscription';
        if (pageName === 'Pipelines Overview') return '/docs/features/pipelines-overview';
        if (pageName === 'Creating a pipeline') return '/docs/features/creating-a-pipeline';
        if (pageName === 'Pipeline list page') return '/docs/features/pipeline-list-page';
        if (pageName === 'Opening a pipeline') return '/docs/features/opening-a-pipeline';
        if (pageName === 'Board and stages') return '/docs/features/board-and-stages';
        if (pageName === 'Adding pipeline items') return '/docs/features/adding-pipeline-items';
        if (pageName === 'Pipeline item cards') return '/docs/features/pipeline-item-cards';
        if (pageName === 'Item detail panel') return '/docs/features/item-detail-panel';
        if (pageName === 'Search, filters, and sort') return '/docs/features/search-filters-and-sort';
        if (pageName === 'General settings') return '/docs/features/pipeline-general-settings';
        if (pageName === 'Stages') return '/docs/features/pipeline-stages';
        if (pageName === 'Card display') return '/docs/features/pipeline-card-display';
        if (pageName === 'Pipeline overview (list card)') return '/docs/features/pipeline-overview-list-card';
        if (pageName === 'Column order') return '/docs/features/pipeline-column-order';
        if (pageName === 'Permissions') return '/docs/features/pipeline-permissions';
        if (pageName === 'Danger zone') return '/docs/features/pipeline-danger-zone';
        if (pageName === 'Adding items in bulk') return '/docs/features/adding-items-in-bulk';
        if (pageName === 'Exporting a pipeline') return '/docs/features/exporting-a-pipeline';
        if (pageName === 'Exporting all pipelines') return '/docs/features/exporting-all-pipelines';
        if (pageName === 'Duplicating a pipeline') return '/docs/features/duplicating-a-pipeline';
        if (pageName === 'Pipeline types (Contacts, Deals, General)') return '/docs/features/pipeline-types-contacts-deals-general';
        if (pageName === 'Won and Lost stages') return '/docs/features/won-and-lost-stages';
        if (pageName === 'Invoices Overview') return '/docs/features/invoices-overview';
        if (pageName === 'Invoices list page') return '/docs/features/invoices-list-page';
        if (pageName === 'Creating and editing an invoice') return '/docs/features/creating-and-editing-an-invoice';
        if (pageName === 'Invoice designer overview') return '/docs/features/invoice-designer-overview';
        if (pageName === 'General, client, and company data') return '/docs/features/invoice-general-client-company-data';
        if (pageName === 'Line items and products') return '/docs/features/invoice-line-items-and-products';
        if (pageName === 'Header, footer, and table appearance') return '/docs/features/invoice-header-footer-table';
        if (pageName === 'Labels, tax, and fees') return '/docs/features/invoice-labels-tax-and-fees';
        if (pageName === 'Signatures and payment options') return '/docs/features/invoice-signatures-and-payment-options';
        if (pageName === 'Sending an invoice by email') return '/docs/features/sending-an-invoice-by-email';
        if (pageName === 'Public invoice link and view invoice page') return '/docs/features/public-invoice-link-and-view-page';
        if (pageName === 'Recording payments and invoice statuses') return '/docs/features/recording-payments-and-invoice-statuses';
        if (pageName === 'Invoice templates') return '/docs/features/invoice-templates';
        if (pageName === 'Booking Pages Overview') return '/docs/features/booking-pages-overview';
        if (pageName === 'Booking pages list') return '/docs/features/booking-pages-list';
        if (pageName === 'Creating a booking page') return '/docs/features/creating-a-booking-page';
        if (pageName === 'Booking page details page') return '/docs/features/booking-page-details-page';
        if (pageName === 'Managing bookings and booking detail modal') return '/docs/features/managing-bookings-and-booking-detail-modal';
        if (pageName === 'Booking page settings overview') return '/docs/features/booking-page-settings-overview';
        if (pageName === 'Basic info, members, and form') return '/docs/features/basic-info-members-and-form';
        if (pageName === 'Limits, appearance, and after booking') return '/docs/features/limits-appearance-and-after-booking';
        if (pageName === 'Booking promo codes') return '/docs/features/booking-promo-codes';
        if (pageName === 'Analytics and optimization insights') return '/docs/features/analytics-and-optimization-insights';
        if (pageName === 'Availability and calendar connections') return '/docs/features/availability-and-calendar-connections';
        if (pageName === 'Public booking page and booker experience') return '/docs/features/public-booking-page-and-booker-experience';
        if (pageName === 'Embedding the booking page') return '/docs/features/embedding-the-booking-page';
        if (pageName === 'Your Day Overview') return '/docs/features/your-day-overview';
        if (pageName === 'Quick Notes') return '/docs/features/quick-notes';
        if (pageName === 'Background jobs') return '/docs/features/background-jobs';
        if (pageName === 'Calendar and team calendars') return '/docs/features/calendar-and-team-calendars';
        if (pageName === 'Dashboard custom metrics') return '/docs/features/dashboard-custom-metrics';
        if (pageName === 'Task Management Overview') return '/docs/features/task-management-overview';
        if (pageName === 'Projects list page') return '/docs/features/projects-list-page';
        if (pageName === 'Opening a project and project tasks page') return '/docs/features/opening-a-project-and-project-tasks-page';
        if (pageName === 'Creating and editing a project') return '/docs/features/creating-and-editing-a-project';
        if (pageName === 'Duplicating and deleting a project') return '/docs/features/duplicating-and-deleting-a-project';
        if (pageName === 'General tasks') return '/docs/features/general-tasks';
        if (pageName === 'Tasks inside a project') return '/docs/features/tasks-inside-a-project';
        if (pageName === 'Creating and editing tasks') return '/docs/features/creating-and-editing-tasks';
        if (pageName === 'Subtasks and reordering') return '/docs/features/subtasks-and-reordering';
        if (pageName === 'Search, filters, and sort (tasks)') return '/docs/features/search-filters-and-sort-tasks';
        if (pageName === 'Whiteboards Overview') return '/docs/features/whiteboards-overview';
        if (pageName === 'Whiteboards list page') return '/docs/features/whiteboards-list-page';
        if (pageName === 'Creating a whiteboard') return '/docs/features/creating-a-whiteboard';
        if (pageName === 'Using the whiteboard canvas') return '/docs/features/using-the-whiteboard-canvas';
        if (pageName === 'Sharing and collaboration') return '/docs/features/sharing-and-collaboration';
        if (pageName === 'Databins Overview') return '/docs/features/databins-overview';
        if (pageName === 'Databins list page') return '/docs/features/databins-list-page';
        if (pageName === 'Creating and editing a databin') return '/docs/features/creating-and-editing-a-databin';
        if (pageName === 'Databin details and requests') return '/docs/features/databin-details-and-requests';
        if (pageName === 'Databin webhook signing') return '/docs/features/databin-webhook-signing';
        if (pageName === 'How databins connect to other features') return '/docs/features/how-databins-connect-to-other-features';
        if (pageName === 'Forms Overview') return '/docs/features/forms-overview';
        if (pageName === 'Forms list page') return '/docs/features/forms-list-page';
        if (pageName === 'Creating and editing a form') return '/docs/features/creating-and-editing-a-form';
        if (pageName === 'Form designer overview') return '/docs/features/form-designer-overview';
        if (pageName === 'Form fields and field types') return '/docs/features/form-fields-and-field-types';
        if (pageName === 'Form settings and appearance') return '/docs/features/form-settings-and-appearance';
        if (pageName === 'Sharing and embedding forms') return '/docs/features/sharing-and-embedding-forms';
        if (pageName === 'Form responses') return '/docs/features/form-responses';
        if (pageName === 'Forms and workflows') return '/docs/features/forms-and-workflows';
        if (pageName === 'Automations Overview') return '/docs/features/automations-overview';
        if (pageName === 'Automations list page') return '/docs/features/automations-list-page';
        if (pageName === 'Creating an automation') return '/docs/features/creating-an-automation';
        if (pageName === 'Workflow designer overview') return '/docs/features/workflow-designer-overview';
        if (pageName === 'Triggers') return '/docs/features/triggers';
        if (pageName === 'Actions and steps') return '/docs/features/actions-and-steps';
        if (pageName === 'Conditions and branching') return '/docs/features/conditions-and-branching';
        if (pageName === 'Variables and data') return '/docs/features/variables-and-data';
        if (pageName === 'Running automations and executions') return '/docs/features/running-automations-and-executions';
        return '/docs/getting-started/introduction'; // Fallback
    }, []);

    const navigateToPage = (pageName) => {
        const url = getUrlFromPage(pageName);
        navigate(url);
        setSelectedItem(pageName);
        setSidebarOpen(false);
    };

    // Generate SEO metadata for each page
    const getPageMetadata = useCallback((pageName) => {
        const baseUrl = 'https://docs.ninjapipe.app';
        const currentUrl = `${baseUrl}${getUrlFromPage(pageName)}`;
        
        const metadata = {
            'Introduction': {
                title: 'Getting Started | NinjaPipe Documentation',
                description: 'Welcome to NinjaPipe documentation. Learn how to manage contacts, pipelines, forms, automations, booking pages, invoices, and more.',
                keywords: 'NinjaPipe documentation, NinjaPipe guide, CRM, contact management, pipelines, forms, automations, booking pages, NinjaPipe help'
            },
            'Quick Start': {
                title: 'Quick Start | NinjaPipe Documentation',
                description: 'Get up and running with NinjaPipe in minutes. Sign up, add your first contact, create a pipeline, and explore features.',
                keywords: 'NinjaPipe quick start, NinjaPipe setup, get started NinjaPipe, NinjaPipe tutorial'
            },
            'Customize the sidebar': {
                title: 'Customize the sidebar | NinjaPipe Documentation',
                description: 'Reorder and group sidebar items, set Top 3 and Shortcuts, hide features, add external links, pin pages, and choose logo home—per workspace.',
                keywords: 'NinjaPipe sidebar, customize sidebar, sidebar layout, pin to sidebar, workspace navigation'
            },
            'Authentication': {
                title: 'API Authentication - Secure API Key Setup | NinjaPipe API',
                description: 'Secure your NinjaPipe CRM API integration with proper authentication. Complete guide to API key setup, Bearer tokens, and security best practices for CRM developers.',
                keywords: 'NinjaPipe CRM API authentication, API key setup, Bearer token, CRM security, API authentication, developer authentication, secure API access, CRM integration security, business API security'
            },
            'Pagination': {
                title: 'API Pagination - Handle Large CRM Datasets | NinjaPipe API',
                description: 'Optimize your NinjaPipe CRM API calls with efficient pagination. Learn to handle large contact lists, deal pipelines, and product catalogs with our comprehensive pagination guide.',
                keywords: 'NinjaPipe CRM API pagination, large CRM datasets, contact pagination, deal pagination, CRM data retrieval, API pagination, business data management, CRM performance optimization'
            },
            'Contact Management': {
                title: 'Contacts Overview | NinjaPipe',
                description: 'Introduction to contacts in NinjaPipe: what they are, where they appear, and how to use the contacts guide.',
                keywords: 'NinjaPipe contacts, contact management, CRM contacts'
            },
            'Finding and Viewing Contacts': {
                title: 'Finding and Viewing Contacts | NinjaPipe',
                description: 'How to find and view your contacts: search, filters, table vs list view, sort, and column visibility.',
                keywords: 'NinjaPipe contacts, contact list, search contacts, filter contacts, contact view'
            },
            'Adding a New Contact': {
                title: 'Adding a New Contact | NinjaPipe',
                description: 'Step-by-step guide to adding a new contact: basic info, company, custom fields, and lead assignment.',
                keywords: 'NinjaPipe add contact, create contact, new contact'
            },
            'The Contact Detail Page': {
                title: 'The Contact Detail Page | NinjaPipe',
                description: 'What you see on a contact page: activities, comments, deals, email, WhatsApp, client portal, archive, duplicate, delete.',
                keywords: 'NinjaPipe contact detail, contact page, contact activities, client portal'
            },
            'Editing a Contact': {
                title: 'Editing a Contact | NinjaPipe',
                description: 'How to edit contact information and what to do if you don\'t have permission.',
                keywords: 'NinjaPipe edit contact, update contact'
            },
            'Bulk Actions': {
                title: 'Bulk Actions for Contacts | NinjaPipe',
                description: 'Update or delete multiple contacts at once: batch update and batch delete.',
                keywords: 'NinjaPipe bulk update contacts, batch delete contacts'
            },
            'Importing Contacts': {
                title: 'Importing Contacts | NinjaPipe',
                description: 'Import contacts from CSV, Excel, or JSON: where to go, column mapping, and importing companies first.',
                keywords: 'NinjaPipe import contacts, CSV import, contact import'
            },
            'Exporting Contacts': {
                title: 'Exporting Contacts | NinjaPipe',
                description: 'Export your contacts to CSV, JSON, or XML from the list or Workspace settings.',
                keywords: 'NinjaPipe export contacts, contact export, CSV export'
            },
            'Duplicates and Merging': {
                title: 'Duplicate Contacts and Merging | NinjaPipe',
                description: 'Find duplicate contacts and merge them into one clean record.',
                keywords: 'NinjaPipe duplicate contacts, merge contacts, deduplicate'
            },
            'Companies and Deals': {
                title: 'Linking Contacts to Companies and Deals | NinjaPipe',
                description: 'Link contacts to companies, create companies from contacts, and connect contacts to deals.',
                keywords: 'NinjaPipe contact company, contact deal, link contact'
            },
            'Custom Fields': {
                title: 'Custom Fields for Contacts | NinjaPipe',
                description: 'Use custom fields for contacts: where they appear and who can manage them.',
                keywords: 'NinjaPipe contact custom fields, custom contact fields'
            },
            'Lead Assignment': {
                title: 'Lead Assignment - Assigned to Me vs All | NinjaPipe',
                description: 'Understand Assigned to me and All views, and how automatic lead assignment works.',
                keywords: 'NinjaPipe lead assignment, assigned to me, contact owner'
            },
            'Archiving Contacts': {
                title: 'Archiving Contacts | NinjaPipe',
                description: 'Archive contacts to hide them from the main list without deleting. View and unarchive.',
                keywords: 'NinjaPipe archive contact, archived contacts'
            },
            'Permissions and Access': {
                title: 'Contact Permissions and Access | NinjaPipe',
                description: 'Who can see and edit contacts. What to do if you don\'t have access.',
                keywords: 'NinjaPipe contact permissions, contact access'
            },
            'Contacts in Lists': {
                title: 'Contacts in Lists | NinjaPipe',
                description: 'Add contacts to lists manually or with rules. Keep lists up to date.',
                keywords: 'NinjaPipe contact lists, add contacts to list'
            },
            'Adding to List': {
                title: 'Adding to List | NinjaPipe',
                description: 'Step-by-step: add contacts to a list from the contacts list, contact page, or list detail page. Static lists only.',
                keywords: 'NinjaPipe add to list, add contact to list, static list'
            },
            'Assigning a Meta Tag': {
                title: 'Assigning a Meta Tag | NinjaPipe',
                description: 'Create meta tags in Workspace Settings and assign them to contacts from the contact detail page. Toggle and remove meta tags.',
                keywords: 'NinjaPipe meta tag, assign meta tag, contact tags'
            },
            'Where Contacts Come From': {
                title: 'Where Contacts Come From | NinjaPipe',
                description: 'Forms, workflows, booking, and inbox: how contacts are created or linked from other features.',
                keywords: 'NinjaPipe contact sources, form to contact, workflow contact'
            },
            'Client Portals Overview': {
                title: 'Client Portals Overview | NinjaPipe',
                description: 'What is the client portal, who can use it, and what clients can do: dashboard, documents, messages, meetings, team, review and approve.',
                keywords: 'NinjaPipe client portal, customer portal, client login, portal overview, client access'
            },
            'Enabling and Managing Access': {
                title: 'Enabling and Managing Client Portal Access | NinjaPipe',
                description: 'Step-by-step: enable the client portal for a contact, set password, view portal, manage password and resend invite, disable access.',
                keywords: 'NinjaPipe enable client portal, manage client portal, portal credentials, disable portal'
            },
            'Sharing Content': {
                title: 'Sharing Content with the Client Portal | NinjaPipe',
                description: 'Where to find client portal controls on the contact page, share documents to the portal, and use the timeline for client-visible content.',
                keywords: 'NinjaPipe share content client portal, share documents to portal, portal controls'
            },
            'Announcements and the Timeline': {
                title: 'Announcements and the Timeline | NinjaPipe',
                description: 'Create announcements on the contact timeline, set type and icon, and make them visible on the client portal dashboard.',
                keywords: 'NinjaPipe announcements, client portal announcements, timeline visible on portal'
            },
            'Approval requests': {
                title: 'Approval Requests | NinjaPipe',
                description: 'Add content approval requests for clients to review and approve from the portal. Clients can approve, request changes, or comment.',
                keywords: 'NinjaPipe approval requests, client portal review approve, content approval'
            },
            'Branding and Settings': {
                title: 'Client Portal Branding and Settings | NinjaPipe',
                description: 'Customize the client portal login page and dashboard: logo, background, colors, welcome and footer text, theme (light/dark).',
                keywords: 'NinjaPipe client portal branding, portal logo, portal theme, portal settings'
            },
            'The Client Portal Experience': {
                title: 'The Client Portal Experience | NinjaPipe',
                description: 'What clients see: logging in, dashboard, documents, messages, meetings, team, review and approve, profile and password.',
                keywords: 'NinjaPipe client portal login, portal dashboard, client experience'
            },
            'Inbox Overview': {
                title: 'Inbox Overview | NinjaPipe',
                description: 'Unified Inbox for email, WhatsApp, and SMS. Channels, threads, and how the Inbox works.',
                keywords: 'NinjaPipe Inbox, unified inbox, email WhatsApp SMS, inbox threads'
            },
            'Connecting Email': {
                title: 'Connecting Email to the Inbox | NinjaPipe',
                description: 'Connect Gmail and Outlook in Account → Integrations. Email threads in the Inbox, sync, and when email is not connected.',
                keywords: 'NinjaPipe Inbox connect email, Gmail Outlook, email integrations'
            },
            'Connecting WhatsApp': {
                title: 'Connecting WhatsApp to the Inbox | NinjaPipe',
                description: 'Connect WhatsApp in Account → Integrations. QR pairing, Inbox threads, contact modal, and Send WhatsApp Message workflow action.',
                keywords: 'NinjaPipe WhatsApp, connect WhatsApp, Inbox, workflow Send WhatsApp Message'
            },
            'Connecting SMS': {
                title: 'Connecting SMS to the Inbox | NinjaPipe',
                description: 'Connect Twilio phone numbers in Account → Integrations. SMS threads in the Inbox and when Twilio is not connected.',
                keywords: 'NinjaPipe Inbox connect SMS, Twilio, SMS integrations'
            },
            'Inbox Settings and Inbox Zero': {
                title: 'Inbox Settings and Inbox Zero | NinjaPipe',
                description: 'Inbox Zero Goal toggle: default to Unread or All threads. Where to find and save Inbox settings.',
                keywords: 'NinjaPipe Inbox settings, Inbox Zero, default filter'
            },
            'Channels, Filters, and Search': {
                title: 'Inbox Channels, Filters, and Search | NinjaPipe',
                description: 'Channel tabs (All, Email, WhatsApp, SMS), Status and Priority filters, and search. URL parameters.',
                keywords: 'NinjaPipe Inbox filters, channel tabs, search messages'
            },
            'Thread List and Reading Messages': {
                title: 'Thread List and Reading Messages | NinjaPipe',
                description: 'Inbox table columns, unread vs read, opening a thread, marking as read, empty states.',
                keywords: 'NinjaPipe Inbox thread list, read messages, thread detail'
            },
            'Syncing and Real-Time Updates': {
                title: 'Inbox Syncing and Real-Time Updates | NinjaPipe',
                description: 'Manual Sync for email, automatic sync on visit, and real-time updates when new messages arrive.',
                keywords: 'NinjaPipe Inbox sync, real-time inbox, email sync'
            },
            'Workspaces Overview': {
                title: 'Workspaces Overview | NinjaPipe',
                description: 'Workspace settings: General (Details, Branding, Client Portal, Domain), Users & Access, Data & Customizations. Email campaigns and email designs are in the main sidebar—not here.',
                keywords: 'NinjaPipe workspace settings, General section, workspace configuration'
            },
            'Workspace Details': {
                title: 'Workspace Details | NinjaPipe',
                description: 'Workspace logo, name, URL, and support email in Workspace Settings under General.',
                keywords: 'NinjaPipe workspace details, workspace logo, support email'
            },
            'Branding & Appearance': {
                title: 'Branding & Appearance | NinjaPipe',
                description: 'App bar logos for dark and light mode, and brand color in Workspace Settings.',
                keywords: 'NinjaPipe branding, app bar logo, brand color'
            },
            'Client Portal Branding': {
                title: 'Client Portal Branding | NinjaPipe',
                description: 'Customize the client portal login page and theme in Workspace Settings. Logo, colors, welcome text.',
                keywords: 'NinjaPipe client portal branding, portal login customization'
            },
            'Domain': {
                title: 'Domain (Custom Domain & CNAME) | NinjaPipe',
                description: 'Connect a custom hostname with CNAME and optional TXT verification. Workspace Settings → Domain. DNS instructions in app.',
                keywords: 'NinjaPipe custom domain, CNAME, workspace domain, DNS verification'
            },
            'Members': {
                title: 'Workspace Members | NinjaPipe',
                description: 'Invite members, assign roles, and manage active, inactive, and removed members in Workspace Settings under Users & Access.',
                keywords: 'NinjaPipe workspace members, invite member, roles, deactivate, remove member'
            },
            'Roles & Permissions': {
                title: 'Roles & Permissions | NinjaPipe',
                description: 'Create and edit roles, set permissions per resource (create, read, update, delete) in Workspace Settings under Users & Access.',
                keywords: 'NinjaPipe roles, permissions, custom role, workspace access'
            },
            'Single Sign-On (SSO)': {
                title: 'Single Sign-On (SSO) | NinjaPipe',
                description: 'Enable SSO for your workspace, upload IdP metadata XML, and submit for review. Enterprise-only. Users & Access in Workspace Settings.',
                keywords: 'NinjaPipe SSO, Single Sign-On, IdP metadata, SAML, enterprise'
            },
            'Currency & Localization': {
                title: 'Currency & Localization | NinjaPipe',
                description: 'Configure currency, number format (US/European), symbol position, and abbreviated vs full numbers in Workspace Settings.',
                keywords: 'NinjaPipe currency, localization, number format, workspace preferences'
            },
            'Data Stores': {
                title: 'Data Stores | NinjaPipe',
                description: 'Create and manage reusable lists (plain text or JSON) for dropdowns and lookups. Data & Customizations in Workspace Settings.',
                keywords: 'NinjaPipe data stores, lists, dropdown options, JSON, workspace'
            },
            'Custom Fields Designer': {
                title: 'Custom Fields Designer | NinjaPipe',
                description: 'Add custom fields: dropdown, text, number, date, file upload, contact/deal pickers. Drag to reorder. Data & Customizations.',
                keywords: 'NinjaPipe custom fields, field designer, dropdown, contact fields'
            },
            'Meta Tags': {
                title: 'Meta Tags | NinjaPipe',
                description: 'Create and manage meta tags (name, icon, color) for contacts, companies, and deals. Assign and filter by tags. Data & Customizations.',
                keywords: 'NinjaPipe meta tags, contact tags, labels, categorize contacts'
            },
            'Customize Views': {
                title: 'Customize Views | NinjaPipe',
                description: 'Add custom sections and fields to contact, deal, company, product, and pipeline views. Drag to reorder. Data & Customizations.',
                keywords: 'NinjaPipe customize views, custom sections, contact detail layout'
            },
            'Email Templates': {
                title: 'Email Designs | NinjaPipe',
                description: 'Create and manage reusable email templates with the visual designer. Drag-and-drop blocks, templates gallery, dynamic parameters. Access from the sidebar.',
                keywords: 'NinjaPipe email designs, email templates, visual designer, placeholders'
            },
            'Email Designs Overview': {
                title: 'Email Designs Overview | NinjaPipe',
                description: 'Create and manage reusable email templates with the visual designer. Drag-and-drop blocks, templates gallery, dynamic parameters. Access from the sidebar.',
                keywords: 'NinjaPipe email designs, email templates, visual designer, placeholders'
            },
            'Email Designs list page': {
                title: 'Email Designs list page | NinjaPipe',
                description: 'The Email Designs list shows all designs in your workspace. Create, search, duplicate, and delete designs. Click a row to open the designer.',
                keywords: 'NinjaPipe email designs list, create design, email templates'
            },
            'Creating an email design': {
                title: 'Creating an email design | NinjaPipe',
                description: 'Create a new email design from the list page. Enter name and description, then design visually or import a template.',
                keywords: 'NinjaPipe create email design, new template, email designer'
            },
            'Email designer overview': {
                title: 'Email designer overview | NinjaPipe',
                description: 'Visual email designer with drag-and-drop blocks, templates gallery, import/export HTML, dynamic parameters.',
                keywords: 'NinjaPipe email designer, drag-and-drop, email templates'
            },
            'Email templates gallery': {
                title: 'Email templates gallery | NinjaPipe',
                description: 'Predefined HTML email templates by category (Sales, Real Estate, Agency, Finance). Import into the designer with one click.',
                keywords: 'NinjaPipe email templates gallery, preset templates, import template'
            },
            'Email Campaigns Overview': {
                title: 'Email Campaigns Overview | NinjaPipe',
                description: 'Send email to Lists from Email campaigns in the sidebar. Not in Workspace settings. Routes /email-campaigns.',
                keywords: 'NinjaPipe email campaigns, list email, broadcast, email marketing'
            },
            'Email Campaigns list page': {
                title: 'Email Campaigns list page | NinjaPipe',
                description: 'All workspace email campaigns at /email-campaigns. Create campaign, search, open detail /email-campaigns/:id.',
                keywords: 'NinjaPipe email campaigns list, create campaign, schedule send'
            },
            'Email Signatures': {
                title: 'Email Signatures | NinjaPipe',
                description: 'Create and manage email signatures: templates, contact details, logo, handwritten signature, brand color. Data & Customizations.',
                keywords: 'NinjaPipe email signatures, signature wizard, default signature'
            },
            'Data Import/Export': {
                title: 'Data Import/Export | NinjaPipe',
                description: 'Import contacts and companies from CSV, JSON, or Excel. Export contacts, companies, and pipelines as CSV or JSON. Data & Customizations.',
                keywords: 'NinjaPipe import export, CSV import, export contacts, backup'
            },
            'Your Data': {
                title: 'Your Data | NinjaPipe',
                description: 'Manage workspace data: clear all data, delete workspace, clear settings, or delete by feature (contacts, deals, etc.). Irreversible. Data & Customizations.',
                keywords: 'NinjaPipe your data, delete workspace, clear data, data management'
            },
            'Workspace Usage': {
                title: 'Workspace Usage | NinjaPipe',
                description: 'View subscription plan limits and current usage: contacts, pipelines, deals, forms, automations, workflow runs. Data & Customizations.',
                keywords: 'NinjaPipe workspace usage, plan limits, subscription usage'
            },
            'NinjaPipe API': {
                title: 'NinjaPipe API | NinjaPipe',
                description: 'Generate and manage API keys for programmatic access to your workspace. Copy, use in requests, deactivate. Data & Customizations.',
                keywords: 'NinjaPipe API, API key, Bearer token, workspace API'
            },
            'MCP (AI Assistants)': {
                title: 'MCP (AI Assistants) | NinjaPipe',
                description: 'Use NinjaPipe in Cursor, Claude Desktop, Windsurf, Manus. MCP config, API key, tools for contacts, deals, projects, pipelines. Data & Customizations.',
                keywords: 'NinjaPipe MCP, AI assistants, Cursor, Claude Desktop, ninjapipe-mcp'
            },
            'List Contacts': {
                title: 'List Contacts API - Retrieve Customer Database | NinjaPipe API',
                description: 'Access your complete customer database with the NinjaPipe CRM List Contacts API. Advanced filtering, sorting, and pagination for large contact lists. Perfect for CRM integrations.',
                keywords: 'NinjaPipe CRM list contacts API, customer database, contact management, CRM contacts, customer data retrieval, business contact API, CRM integration, customer relationship management, contact filtering, business contacts'
            },
            'Create Contact': {
                title: 'Create Contact API - Add New Customers | NinjaPipe API',
                description: 'Add new customers to your CRM with the NinjaPipe Create Contact API. Complete guide with examples for customer onboarding, lead capture, and CRM data entry automation.',
                keywords: 'NinjaPipe CRM create contact API, add customer, new contact, customer onboarding, lead capture, CRM data entry, business contact creation, customer management, CRM automation, contact import'
            },
            'Get Contact': {
                title: 'Get Contact API - Retrieve Customer Details | NinjaPipe API',
                description: 'Retrieve detailed customer information with the NinjaPipe Get Contact API. Perfect for customer lookup, profile management, and CRM integrations.',
                keywords: 'NinjaPipe CRM get contact API, customer details, contact lookup, customer profile, CRM contact retrieval, business contact API, customer information, contact management, CRM data access'
            },
            'Update Contact': {
                title: 'Update Contact API - Modify Customer Information | NinjaPipe API',
                description: 'Update customer information in your CRM with the NinjaPipe Update Contact API. Complete guide for customer data management, profile updates, and CRM maintenance.',
                keywords: 'NinjaPipe CRM update contact API, modify customer, edit contact, customer data update, CRM contact management, business contact editing, customer profile update, CRM data modification, contact maintenance'
            },
            'Delete Contact': {
                title: 'Delete Contact API - Remove Customer Records | NinjaPipe API',
                description: 'Remove customer records from your CRM with the NinjaPipe Delete Contact API. Complete guide for data cleanup, GDPR compliance, and CRM maintenance.',
                keywords: 'NinjaPipe CRM delete contact API, remove customer, delete contact, customer data removal, CRM contact deletion, business contact management, GDPR compliance, CRM data cleanup, contact maintenance'
            },
            'List Deals': {
                title: 'List Deals API - Access Sales Pipeline | NinjaPipe API',
                description: 'Access your complete sales pipeline with the NinjaPipe CRM List Deals API. Advanced filtering, sorting, and pagination for deal management. Perfect for sales CRM integrations.',
                keywords: 'NinjaPipe CRM list deals API, sales pipeline, deal management, CRM deals, sales opportunities, business deals, sales tracking, CRM sales data, deal filtering, sales automation'
            },
            'Create Deal': {
                title: 'Create Deal API - Add Sales Opportunities | NinjaPipe API',
                description: 'Create new sales opportunities with the NinjaPipe CRM Create Deal API. Complete guide for sales pipeline management, lead conversion, and CRM deal tracking.',
                keywords: 'NinjaPipe CRM create deal API, add sales opportunity, new deal, sales pipeline, lead conversion, CRM deal creation, business sales, sales tracking, deal management, sales automation'
            },
            'Get Deal': {
                title: 'Get Deal API - Retrieve Sales Opportunity | NinjaPipe API',
                description: 'Retrieve detailed sales opportunity information with the NinjaPipe Get Deal API. Perfect for deal lookup, sales tracking, and CRM integrations.',
                keywords: 'NinjaPipe CRM get deal API, sales opportunity details, deal lookup, sales tracking, CRM deal retrieval, business deal API, sales information, deal management, CRM data access, sales pipeline'
            },
            'Update Deal': {
                title: 'Update Deal API - Modify Sales Opportunities | NinjaPipe API',
                description: 'Update sales opportunities in your CRM with the NinjaPipe Update Deal API. Complete guide for deal progression, sales tracking, and CRM maintenance.',
                keywords: 'NinjaPipe CRM update deal API, modify sales opportunity, edit deal, sales progression, CRM deal management, business deal editing, sales tracking, deal updates, CRM data modification'
            },
            'Delete Deal': {
                title: 'Delete Deal API - Remove Sales Opportunities | NinjaPipe API',
                description: 'Remove sales opportunities from your CRM with the NinjaPipe Delete Deal API. Complete guide for deal cleanup, sales pipeline management, and CRM maintenance.',
                keywords: 'NinjaPipe CRM delete deal API, remove sales opportunity, delete deal, sales pipeline cleanup, CRM deal deletion, business deal management, sales data cleanup, deal maintenance'
            },
            'List Products': {
                title: 'List Products API - Access Product Catalog | NinjaPipe API',
                description: 'Access your complete product catalog with the NinjaPipe CRM List Products API. Advanced filtering, sorting, and pagination for product management. Perfect for e-commerce CRM integrations.',
                keywords: 'NinjaPipe CRM list products API, product catalog, product management, CRM products, business products, product inventory, e-commerce CRM, product data, catalog management, business automation'
            },
            'Create Product': {
                title: 'Create Product API - Add New Products | NinjaPipe API',
                description: 'Add new products to your CRM catalog with the NinjaPipe Create Product API. Complete guide for product onboarding, inventory management, and e-commerce CRM integration.',
                keywords: 'NinjaPipe CRM create product API, add product, new product, product catalog, inventory management, e-commerce CRM, product onboarding, business product creation, catalog automation, product data entry'
            },
            'Get Product': {
                title: 'Get Product API - Retrieve Product Details | NinjaPipe API',
                description: 'Retrieve detailed product information with the NinjaPipe Get Product API. Perfect for product lookup, catalog management, and e-commerce CRM integrations.',
                keywords: 'NinjaPipe CRM get product API, product details, product lookup, product catalog, CRM product retrieval, business product API, product information, catalog management, e-commerce data access'
            },
            'Update Product': {
                title: 'Update Product API - Modify Product Information | NinjaPipe API',
                description: 'Update product information in your CRM with the NinjaPipe Update Product API. Complete guide for product management, catalog updates, and e-commerce CRM maintenance.',
                keywords: 'NinjaPipe CRM update product API, modify product, edit product, product catalog update, CRM product management, business product editing, catalog maintenance, product data modification'
            },
            'Delete Product': {
                title: 'Delete Product API - Remove Products | NinjaPipe API',
                description: 'Remove products from your CRM catalog with the NinjaPipe Delete Product API. Complete guide for product cleanup, catalog management, and e-commerce CRM maintenance.',
                keywords: 'NinjaPipe CRM delete product API, remove product, delete product, product catalog cleanup, CRM product deletion, business product management, catalog maintenance, product data cleanup'
            },
            'List Budgets': {
                title: 'List Budgets API - Access Financial Planning | NinjaPipe API',
                description: 'Access your complete budget management system with the NinjaPipe CRM List Budgets API. Advanced filtering, sorting, and pagination for financial planning. Perfect for business CRM integrations.',
                keywords: 'NinjaPipe CRM list budgets API, budget management, financial planning, CRM budgets, business budgets, budget tracking, financial CRM, budget data, financial automation, business planning'
            },
            'Create Budget': {
                title: 'Create Budget API - Add Financial Plans | NinjaPipe API',
                description: 'Create new budgets and financial plans with the NinjaPipe CRM Create Budget API. Complete guide for financial planning, budget management, and business CRM integration.',
                keywords: 'NinjaPipe CRM create budget API, add budget, financial planning, budget creation, financial planning, business budget management, budget automation, financial CRM, business planning'
            },
            'Get Budget': {
                title: 'Get Budget API - Retrieve Financial Plan | NinjaPipe API',
                description: 'Retrieve detailed budget information with the NinjaPipe Get Budget API. Perfect for financial planning, budget tracking, and business CRM integrations.',
                keywords: 'NinjaPipe CRM get budget API, budget details, financial plan lookup, budget tracking, CRM budget retrieval, business budget API, financial information, budget management, financial data access'
            },
            'Update Budget': {
                title: 'Update Budget API - Modify Financial Plans | NinjaPipe API',
                description: 'Update budget information in your CRM with the NinjaPipe Update Budget API. Complete guide for financial planning, budget management, and business CRM maintenance.',
                keywords: 'NinjaPipe CRM update budget API, modify budget, edit budget, financial plan update, CRM budget management, business budget editing, financial tracking, budget updates, financial data modification'
            },
            'Delete Budget': {
                title: 'Delete Budget API - Remove Financial Plans | NinjaPipe API',
                description: 'Remove budgets from your CRM with the NinjaPipe Delete Budget API. Complete guide for financial cleanup, budget management, and business CRM maintenance.',
                keywords: 'NinjaPipe CRM delete budget API, remove budget, delete budget, financial plan cleanup, CRM budget deletion, business budget management, financial data cleanup, budget maintenance'
            },
            'List Budget Expenses': {
                title: 'List Budget Expenses API - Track Financial Spending | NinjaPipe API',
                description: 'Track and manage budget expenses with the NinjaPipe CRM List Budget Expenses API. Advanced filtering, sorting, and pagination for expense management. Perfect for financial CRM integrations.',
                keywords: 'NinjaPipe CRM list budget expenses API, expense tracking, budget expenses, financial spending, expense management, budget tracking, financial CRM, expense data, business expenses, financial automation'
            },
            'Create Budget Expense': {
                title: 'Create Budget Expense API - Add Financial Expenses | NinjaPipe API',
                description: 'Add new budget expenses with the NinjaPipe CRM Create Budget Expense API. Complete guide for expense tracking, financial planning, and business CRM integration.',
                keywords: 'NinjaPipe CRM create budget expense API, add expense, expense tracking, budget expense creation, financial planning, business expense management, expense automation, financial CRM, business spending'
            },
            'Get Budget Expense': {
                title: 'Get Budget Expense API - Retrieve Expense Details | NinjaPipe API',
                description: 'Retrieve detailed budget expense information with the NinjaPipe Get Budget Expense API. Perfect for expense lookup, financial tracking, and business CRM integrations.',
                keywords: 'NinjaPipe CRM get budget expense API, expense details, expense lookup, budget expense retrieval, financial tracking, business expense API, expense information, financial data access, expense management'
            },
            'Update Budget Expense': {
                title: 'Update Budget Expense API - Modify Financial Expenses | NinjaPipe API',
                description: 'Update budget expenses in your CRM with the NinjaPipe Update Budget Expense API. Complete guide for expense management, financial tracking, and business CRM maintenance.',
                keywords: 'NinjaPipe CRM update budget expense API, modify expense, edit expense, budget expense update, financial tracking, business expense management, expense updates, financial data modification, expense maintenance'
            },
            'Delete Budget Expense': {
                title: 'Delete Budget Expense API - Remove Financial Expenses | NinjaPipe API',
                description: 'Remove budget expenses from your CRM with the NinjaPipe Delete Budget Expense API. Complete guide for expense cleanup, financial management, and business CRM maintenance.',
                keywords: 'NinjaPipe CRM delete budget expense API, remove expense, delete expense, budget expense cleanup, financial expense deletion, business expense management, expense data cleanup, financial maintenance'
            },
            'SSO Setup': {
                title: 'SSO Setup - Get Your IdP Metadata XML | NinjaPipe',
                description: 'How to get your identity provider (IdP) metadata XML file for NinjaPipe Single Sign-On. Step-by-step for Google Workspace, Okta, Azure AD, and other SAML 2.0 IdPs.',
                keywords: 'NinjaPipe SSO, Single Sign-On setup, SAML IdP metadata, Google Workspace SSO, Okta SSO, Azure AD SSO, enterprise SSO, IdP metadata XML'
            },
            'Account details': {
                title: 'Account Details - Profile & Settings | NinjaPipe',
                description: 'Update your NinjaPipe account profile: name, phone, timezone, profile photo, and language. Access account settings from the app.',
                keywords: 'NinjaPipe account, profile settings, account details, timezone, language, My Profile'
            },
            'Integrations': {
                title: 'Account Integrations - Calendar, Email, Zoom, Teams | NinjaPipe',
                description: 'Connect your account to Google Calendar, Outlook, Gmail, Outlook Email, Zoom, Microsoft Teams, WhatsApp, and SMS. Manage OAuth connections per workspace.',
                keywords: 'NinjaPipe integrations, Google Calendar, Outlook, Zoom, Teams, email connection, account integrations'
            },
            'Security': {
                title: 'Security - Password & Two-Factor Authentication | NinjaPipe',
                description: 'Change your password, enable two-factor authentication (2FA) with Microsoft Authenticator, manage passkeys and active sessions for your NinjaPipe account.',
                keywords: 'NinjaPipe security, 2FA, two-factor authentication, password change, passkeys, active sessions'
            },
            'Licenses': {
                title: 'Licenses - License Keys | NinjaPipe',
                description: 'View and copy your NinjaPipe license keys for Solo, Business, or AppSumo plans. Manage license information in your account.',
                keywords: 'NinjaPipe licenses, license key, subscription license, account licenses'
            },
            'Billing & Subscription': {
                title: 'Billing & Subscription - Plans and Payments | NinjaPipe',
                description: 'Manage your NinjaPipe subscription: upgrade or change plan, update seats, view free trial, and handle billing. Stripe-powered payments.',
                keywords: 'NinjaPipe billing, subscription, upgrade plan, seats, free trial, Stripe, payment'
            },
            'Pipelines Overview': {
                title: 'Pipelines Overview - Kanban Workflows | NinjaPipe',
                description: 'What pipelines are in NinjaPipe: types (Contacts, Deals, General), stages, and pipeline items. When to use each type and where to find pipelines.',
                keywords: 'NinjaPipe pipelines, Kanban, deals pipeline, contacts pipeline, workflow, stages'
            },
            'Creating a pipeline': {
                title: 'Creating a Pipeline - Name, Type, Stages | NinjaPipe',
                description: 'Create a new pipeline: name, description, type (Contacts, Deals, General), and default stages. Step-by-step with Create Pipeline modal.',
                keywords: 'NinjaPipe create pipeline, new pipeline, pipeline type, pipeline stages'
            },
            'Pipeline list page': {
                title: 'Pipeline List Page - Metrics, View, Filters | NinjaPipe',
                description: 'Pipelines list: metrics, grid and table view, search, filters by type and status, sort, pipeline cards with stage summary and conversion.',
                keywords: 'NinjaPipe pipeline list, pipeline metrics, pipeline filters, pipeline view'
            },
            'Opening a pipeline': {
                title: 'Opening a Pipeline - URL and Navigation | NinjaPipe',
                description: 'How to open a pipeline by URL (slug), breadcrumb, and header. Navigate from the pipelines list to a single pipeline board.',
                keywords: 'NinjaPipe open pipeline, pipeline URL, pipeline slug, pipeline board'
            },
            'Board and stages': {
                title: 'Board and Stages - Kanban Columns | NinjaPipe',
                description: 'Pipeline board: columns are stages, stage colors and counts, Won and Lost badges. Reorder columns in settings.',
                keywords: 'NinjaPipe pipeline board, Kanban columns, stages, Won Lost'
            },
            'Adding pipeline items': {
                title: 'Adding Pipeline Items - Manual and Linked | NinjaPipe',
                description: 'Add items to a pipeline: manual fields or link Contact, Deal, Account, Company, Product. Add button and per-stage add. Choose initial stage.',
                keywords: 'NinjaPipe add pipeline item, pipeline card, link contact deal, pipeline item'
            },
            'Pipeline item cards': {
                title: 'Pipeline Item Cards - Title, Value, Priority | NinjaPipe',
                description: 'What appears on pipeline cards: title, value, priority, assignee, tags. Inline edit and drag-and-drop between stages.',
                keywords: 'NinjaPipe pipeline card, card display, drag and drop pipeline'
            },
            'Item detail panel': {
                title: 'Item Detail Panel - Checklist, Comments, Custom Fields | NinjaPipe',
                description: 'Pipeline item sidebar: edit fields, checklist, comments with replies and @mentions, attachments, custom fields, history. Duplicate or delete item.',
                keywords: 'NinjaPipe pipeline item detail, checklist, comments, pipeline sidebar'
            },
            'Search, filters, and sort': {
                title: 'Search, Filters, and Sort in Pipelines | NinjaPipe',
                description: 'Search pipeline items, filter by priority, tags, assignee, stage, has value, has due date. URL persistence. Sort options.',
                keywords: 'NinjaPipe pipeline search, pipeline filters, pipeline sort'
            },
            'General settings': {
                title: 'Pipeline General Settings - Name, Icon, Color | NinjaPipe',
                description: 'Pipeline settings: general tab for name, description, icon, and color. Where to find the settings modal.',
                keywords: 'NinjaPipe pipeline settings, pipeline name, pipeline icon'
            },
            'Stages': {
                title: 'Pipeline Stages - Add, Edit, Won and Lost | NinjaPipe',
                description: 'Manage pipeline stages: add, edit name and color, delete. Set one Won stage and one Lost stage for conversion.',
                keywords: 'NinjaPipe pipeline stages, Won stage, Lost stage, stage color'
            },
            'Card display': {
                title: 'Pipeline Card Display Settings | NinjaPipe',
                description: 'Configure what appears on pipeline cards: avatar, subtitle, description, tags, assignee, last activity, priority, value, compact mode, title field.',
                keywords: 'NinjaPipe card display, pipeline card settings'
            },
            'Pipeline overview (list card)': {
                title: 'Pipeline Overview (List Card) - Stage Summary | NinjaPipe',
                description: 'Pipeline overview settings: which stages to show in the pipeline card on the list page. Conversion label.',
                keywords: 'NinjaPipe pipeline overview card, stage summary, list card'
            },
            'Column order': {
                title: 'Pipeline Column Order - Reorder Stages | NinjaPipe',
                description: 'Reorder pipeline stages (columns) by dragging in the Column Order settings tab.',
                keywords: 'NinjaPipe column order, reorder stages, pipeline columns'
            },
            'Permissions': {
                title: 'Pipeline Permissions - Who Can Access | NinjaPipe',
                description: 'Pipeline members: restrict who can access this pipeline. Select team members. Owner always included. Empty = all workspace members.',
                keywords: 'NinjaPipe pipeline permissions, pipeline members, pipeline access'
            },
            'Danger zone': {
                title: 'Pipeline Danger Zone - Clear Column, Delete | NinjaPipe',
                description: 'Pipeline danger zone: clear a column (move items out), clear all cards, delete pipeline. Irreversible actions.',
                keywords: 'NinjaPipe pipeline danger zone, delete pipeline, clear column'
            },
            'Adding items in bulk': {
                title: 'Adding Pipeline Items in Bulk - CSV, Google Sheets | NinjaPipe',
                description: 'Bulk upload pipeline items: CSV, Google Sheets, or manual column assignment. Map rows to stages.',
                keywords: 'NinjaPipe bulk upload pipeline, CSV import pipeline, Google Sheets pipeline'
            },
            'Exporting a pipeline': {
                title: 'Export a Pipeline - JSON and CSV | NinjaPipe',
                description: 'Export the current pipeline to JSON or CSV from the pipeline menu. Metadata, stages, cards, and settings.',
                keywords: 'NinjaPipe export pipeline, pipeline JSON, pipeline CSV'
            },
            'Exporting all pipelines': {
                title: 'Export All Pipelines - JSON and CSV | NinjaPipe',
                description: 'Export all pipelines in the workspace to JSON or CSV from the pipelines list page.',
                keywords: 'NinjaPipe export all pipelines, pipelines export'
            },
            'Duplicating a pipeline': {
                title: 'Duplicating a Pipeline - Copy Stages and Items | NinjaPipe',
                description: 'Duplicate a pipeline from the list or pipeline menu. Copies pipeline, stages, and all items. New slug and IDs.',
                keywords: 'NinjaPipe duplicate pipeline, copy pipeline'
            },
            'Pipeline types (Contacts, Deals, General)': {
                title: 'Pipeline Types - Contacts, Deals, General | NinjaPipe',
                description: 'Difference between Contacts, Deals, and General pipelines. Default stages and typical use for each type.',
                keywords: 'NinjaPipe pipeline types, Contacts pipeline, Deals pipeline, General pipeline'
            },
            'Won and Lost stages': {
                title: 'Won and Lost Stages - Conversion Rate | NinjaPipe',
                description: 'Mark one stage as Won and one as Lost. How conversion rate is calculated and where it appears (list, overview card, export).',
                keywords: 'NinjaPipe Won stage, Lost stage, conversion rate, pipeline conversion'
            },
            'Invoices Overview': {
                title: 'Invoices Overview - Create, Send, Get Paid | NinjaPipe',
                description: 'Overview of NinjaPipe invoices: list, designer, public view, statuses, and payment. Create professional invoices and get paid online.',
                keywords: 'NinjaPipe invoices, invoice list, invoice designer, invoice status, payment'
            },
            'Invoices list page': {
                title: 'Invoices List Page - Search, Actions, Status | NinjaPipe',
                description: 'Manage all invoices from the list: search, filter by status, edit, send email, download PDF, copy link, mark sent, record payment, void, delete.',
                keywords: 'NinjaPipe invoice list, invoice actions, invoice status, send invoice'
            },
            'Creating and editing an invoice': {
                title: 'Creating and Editing an Invoice | NinjaPipe',
                description: 'Create a new invoice or edit an existing one. Save, document link (slug), and open in the invoice designer.',
                keywords: 'NinjaPipe create invoice, edit invoice, save invoice, document link'
            },
            'Invoice designer overview': {
                title: 'Invoice Designer Overview - Layout and Sections | NinjaPipe',
                description: 'Full-page invoice designer: sidebar sections, live preview, save, save as template, load template, and view/copy public link.',
                keywords: 'NinjaPipe invoice designer, invoice sections, invoice preview, template'
            },
            'General, client, and company data': {
                title: 'Invoice General, Client, and Company Data | NinjaPipe',
                description: 'Set paper size, orientation, margins, font, document link. Bill-to contact and company info (name, address, phone, Invoice To).',
                keywords: 'NinjaPipe invoice general, bill-to, company info, document link'
            },
            'Line items and products': {
                title: 'Invoice Line Items and Products | NinjaPipe',
                description: 'Add and edit line items: name, quantity, rate, discount, description, SKU. Add from product catalog for faster invoicing.',
                keywords: 'NinjaPipe invoice line items, products, quantity, rate, discount'
            },
            'Header, footer, and table appearance': {
                title: 'Invoice Header, Footer, and Table Appearance | NinjaPipe',
                description: 'Logo, document title, invoice number, date. Footer content. Table borders, header/row colors, row separators.',
                keywords: 'NinjaPipe invoice header, footer, table, logo, invoice number'
            },
            'Labels, tax, and fees': {
                title: 'Invoice Labels, Tax, and Fees | NinjaPipe',
                description: 'Terms, due date, P.O.#, salesperson, project, subject. Tax and fee items and how they are calculated.',
                keywords: 'NinjaPipe invoice labels, tax, fees, due date, terms'
            },
            'Signatures and payment options': {
                title: 'Invoice Signatures and Payment Options | NinjaPipe',
                description: 'Signature blocks: draw or upload, per-row layout, border. Enable Stripe and PayPal for online payment on the view-invoice page.',
                keywords: 'NinjaPipe invoice signatures, Stripe, PayPal, payment options'
            },
            'Sending an invoice by email': {
                title: 'Sending an Invoice by Email | NinjaPipe',
                description: 'Send an invoice to the client by email from the list or designer. Email connection and template.',
                keywords: 'NinjaPipe send invoice, email invoice, invoice email'
            },
            'Public invoice link and view invoice page': {
                title: 'Public Invoice Link and View Invoice Page | NinjaPipe',
                description: 'Share the view-invoice URL (by ID or workspace slug + invoice slug). Client can view, pay, sign, download PDF, print. Theme toggle.',
                keywords: 'NinjaPipe view invoice, public invoice link, pay invoice, sign invoice'
            },
            'Recording payments and invoice statuses': {
                title: 'Recording Payments and Invoice Statuses | NinjaPipe',
                description: 'Record payment from the list. Statuses: Draft, Paid, Partially paid, Pending Approval. How status is derived from payments.',
                keywords: 'NinjaPipe record payment, invoice status, Paid, Partially paid'
            },
            'Invoice templates': {
                title: 'Invoice Templates - Save, Load, Import | NinjaPipe',
                description: 'Save current design as a template. Load a template to start a new invoice. Import selected categories from a template.',
                keywords: 'NinjaPipe invoice templates, save template, load template, import template'
            },
            'Booking Pages Overview': {
                title: 'Booking Pages Overview - Scheduling & Meetings | NinjaPipe',
                description: 'Overview of NinjaPipe booking pages: create shareable scheduling pages, connect calendar and meeting apps, manage bookings and settings.',
                keywords: 'NinjaPipe booking pages, scheduling, meetings, calendar, availability'
            },
            'Booking pages list': {
                title: 'Booking Pages List - Manage All Booking Pages | NinjaPipe',
                description: 'List of all booking pages: search, filter by status, grid/table view, create, availability, copy link, open, delete.',
                keywords: 'NinjaPipe booking pages list, manage booking pages, create booking page'
            },
            'Creating a booking page': {
                title: 'Creating a Booking Page | NinjaPipe',
                description: 'Create a new booking page: name, slug, description. Then configure settings, members, availability, and share the link.',
                keywords: 'NinjaPipe create booking page, new booking page'
            },
            'Booking page details page': {
                title: 'Booking Page Details Page | NinjaPipe',
                description: 'Detail view for a booking page: stats, bookings list, settings modal, share modal, optimization insights, dropoff analytics.',
                keywords: 'NinjaPipe booking page details, booking page stats, bookings'
            },
            'Managing bookings and booking detail modal': {
                title: 'Managing Bookings and Booking Detail Modal | NinjaPipe',
                description: 'View and manage bookings from the details page. Open booking detail modal to see booker info, status, reassign, edit, update status.',
                keywords: 'NinjaPipe manage bookings, booking detail, confirm cancel no-show'
            },
            'Booking page settings overview': {
                title: 'Booking Page Settings Overview | NinjaPipe',
                description: 'Settings modal and sections: Basic Info, Members, Form, Limits & Buffers, Appearance, After Booking, Confirmation Email, Analytics.',
                keywords: 'NinjaPipe booking page settings, booking settings modal'
            },
            'Basic info, members, and form': {
                title: 'Basic Info, Members, and Form Settings | NinjaPipe',
                description: 'Booking page basic info (name, slug, description, active). Assign members (manual, round robin, load balancing). Link a form for booker fields.',
                keywords: 'NinjaPipe booking members, assignment, linked form'
            },
            'Limits, appearance, and after booking': {
                title: 'Limits, Appearance, and After Booking | NinjaPipe',
                description: 'Duration, buffer time. Theme, background, logo, primary color, meeting types (Meet, Zoom, Teams, Phone, In-Person, Custom). After booking: message or redirect. Confirmation email.',
                keywords: 'NinjaPipe booking duration, buffer, appearance, meeting type, confirmation'
            },
            'Analytics and optimization insights': {
                title: 'Analytics and Optimization Insights | NinjaPipe',
                description: 'Booking page analytics: show optimization insights, dropoff rates by step. Disable insights from the details page if desired.',
                keywords: 'NinjaPipe booking analytics, optimization insights, dropoff'
            },
            'Availability and calendar connections': {
                title: 'Availability and Calendar Connections | NinjaPipe',
                description: 'Set your working hours and availability (meeting_availability). Connect Google Calendar or Outlook; connect Zoom, Teams, or Google Meet for video.',
                keywords: 'NinjaPipe availability, working hours, calendar connection, meeting app'
            },
            'Public booking page and booker experience': {
                title: 'Public Booking Page and Booker Experience | NinjaPipe',
                description: 'Shareable link /book/:workspaceSlug/:slug. Booker flow: select date, time, meeting type, fill form, confirm. Reschedule, theme, visit tracking.',
                keywords: 'NinjaPipe public booking, booker experience, share booking link'
            },
            'Embedding the booking page': {
                title: 'Embedding the Booking Page | NinjaPipe',
                description: 'Use the embed URL /book/:workspaceSlug/:slug/embed to show the booking page inside an iframe on your website.',
                keywords: 'NinjaPipe embed booking page, iframe booking'
            },
            'Your Day Overview': {
                title: 'Your Day Overview - Tasks, Events & Stale Leads | NinjaPipe',
                description: 'Your Day shows a unified view of tasks, calendar events, and stale pipeline items. Check items off, filter by type, and jump to tasks, deals, or events.',
                keywords: 'NinjaPipe Your Day, daily view, tasks, events, stale leads, pipeline follow-up'
            },
            'Quick Notes': {
                title: 'Quick Notes - Sticky Notes in NinjaPipe | NinjaPipe',
                description: 'Private workspace-scoped sticky notes in the top bar. Draggable post-its, rich text, pinning, and a My Notes sidebar pane.',
                keywords: 'NinjaPipe Quick Notes, sticky notes, personal notes, top bar'
            },
            'Background jobs': {
                title: 'Background Jobs - Long-Running Tasks | NinjaPipe',
                description: 'Track bulk list operations and other long-running workspace jobs from the top bar jobs menu with progress and dismiss.',
                keywords: 'NinjaPipe background jobs, bulk add list, job progress'
            },
            'Calendar and team calendars': {
                title: 'Calendar and Team Calendars | NinjaPipe',
                description: 'View team member calendars, assign events to members, and generate Microsoft Teams meeting links with Outlook.',
                keywords: 'NinjaPipe calendar, team calendars, event assignees, Teams meeting'
            },
            'Dashboard custom metrics': {
                title: 'Dashboard Custom Metrics | NinjaPipe',
                description: 'Build live stat cards on the CRM dashboard with sentence builder, rollups, and per-record formulas.',
                keywords: 'NinjaPipe dashboard, custom metrics, sentence builder, rollups'
            },
            'Copying workspace settings': {
                title: 'Copying Workspace Settings | NinjaPipe',
                description: 'Clone configuration from an existing workspace during onboarding: meta tags, custom fields, booking pages, invoice designer, and more.',
                keywords: 'NinjaPipe workspace copy, clone settings, onboarding'
            },
            'Client portal emails': {
                title: 'Client Portal Emails | NinjaPipe',
                description: 'Customize client portal notification emails: welcome, password reset, booking confirmations. Email designer in workspace settings.',
                keywords: 'NinjaPipe client portal emails, portal notifications, email designer'
            },
            'Booking promo codes': {
                title: 'Booking Promo Codes | NinjaPipe',
                description: 'Create promo codes for paid booking pages. Bookers apply discounts at checkout with Stripe.',
                keywords: 'NinjaPipe booking promo codes, discount codes, paid bookings'
            },
            'Task Management Overview': {
                title: 'Task Management Overview - Projects & Tasks | NinjaPipe',
                description: 'Overview of NinjaPipe task management: projects tab, general tasks tab, project tasks, create and edit tasks, subtasks, drag-and-drop, search and filters.',
                keywords: 'NinjaPipe task management, projects, general tasks, tasks, subtasks'
            },
            'General tasks': {
                title: 'General Tasks - Workspace Tasks | NinjaPipe',
                description: 'Create and manage workspace-level general tasks not tied to a project. Tasks tab, add task, subtasks, discussions, automations, and API.',
                keywords: 'NinjaPipe general tasks, workspace tasks, tasks tab'
            },
            'Projects list page': {
                title: 'Projects List Page - Manage Projects | NinjaPipe',
                description: 'Tasks page at /tasks: list of projects with progress, status, priority. Search, filter, sort, create, edit, duplicate, delete.',
                keywords: 'NinjaPipe projects list, tasks page, project progress'
            },
            'Opening a project and project tasks page': {
                title: 'Opening a Project and Project Tasks Page | NinjaPipe',
                description: 'Open a project from the list: project dialog or navigate to /project/:projectId/tasks to view and manage tasks.',
                keywords: 'NinjaPipe open project, project tasks page'
            },
            'Creating and editing a project': {
                title: 'Creating and Editing a Project | NinjaPipe',
                description: 'Create a new project (name, description, status, priority, due date, team, tags). Edit project details from the list or project dialog.',
                keywords: 'NinjaPipe create project, edit project'
            },
            'Duplicating and deleting a project': {
                title: 'Duplicating and Deleting a Project | NinjaPipe',
                description: 'Duplicate a project (copies project and all tasks, preserves hierarchy). Delete a project (removes project and its tasks).',
                keywords: 'NinjaPipe duplicate project, delete project'
            },
            'Tasks inside a project': {
                title: 'Tasks Inside a Project | NinjaPipe',
                description: 'Project tasks page: list of tasks with status, priority, assignees, due date. Progress bar, search, filters, task menu.',
                keywords: 'NinjaPipe project tasks, task list, task status'
            },
            'Creating and editing tasks': {
                title: 'Creating and Editing Tasks | NinjaPipe',
                description: 'Create Task modal: title, description, status, priority, assignees, due date, estimated hours. Create subtasks. Edit and save.',
                keywords: 'NinjaPipe create task, edit task, CreateTaskModal'
            },
            'Subtasks and reordering': {
                title: 'Subtasks and Reordering | NinjaPipe',
                description: 'Task hierarchy with parent_id. Add subtask from task menu. Expand/collapse. Drag-and-drop to reorder or move task as subtask.',
                keywords: 'NinjaPipe subtasks, task hierarchy, drag and drop'
            },
            'Search, filters, and sort (tasks)': {
                title: 'Search, Filters, and Sort (Tasks) | NinjaPipe',
                description: 'Search and filter projects (by status, priority). Search and filter tasks inside a project. Sort by name, progress, due date, created.',
                keywords: 'NinjaPipe task search, task filters, sort tasks'
            },
            'Whiteboards Overview': {
                title: 'Whiteboards Overview - Collaborative Canvas | NinjaPipe',
                description: 'Overview of NinjaPipe whiteboards: infinite canvas for diagrams, brainstorming, and real-time collaboration. Use cases and how it fits your workflow.',
                keywords: 'NinjaPipe whiteboards, collaborative whiteboard, canvas, brainstorming, Miro alternative'
            },
            'Whiteboards list page': {
                title: 'Whiteboards List Page | NinjaPipe',
                description: 'Whiteboards list at /whiteboards: all boards in the workspace. Search by name or slug, create, open, edit, duplicate, delete.',
                keywords: 'NinjaPipe whiteboards list, whiteboard management'
            },
            'Creating a whiteboard': {
                title: 'Creating a Whiteboard | NinjaPipe',
                description: 'Create or edit a whiteboard: name and slug (unique per workspace). Slug is used in the board URL.',
                keywords: 'NinjaPipe create whiteboard, whiteboard name, whiteboard slug'
            },
            'Using the whiteboard canvas': {
                title: 'Using the Whiteboard Canvas | NinjaPipe',
                description: 'Draw shapes, text, arrows on the canvas. Zoom, pan, auto-save. Real-time sync so collaborators see changes.',
                keywords: 'NinjaPipe whiteboard canvas, draw, shapes, zoom, pan, Excalidraw'
            },
            'Sharing and collaboration': {
                title: 'Sharing and Collaboration | NinjaPipe',
                description: 'Share whiteboard link with your team. Anyone with the link can view and edit. See collaborators and their cursors in real time.',
                keywords: 'NinjaPipe whiteboard share, whiteboard collaboration, real-time whiteboard'
            },
            'Databins Overview': {
                title: 'Databins Overview - Webhook Data Collection | NinjaPipe',
                description: 'Overview of NinjaPipe databins: collect data from forms, apps, and ads via a unique URL. Use with workflows, lead campaigns, and automation.',
                keywords: 'NinjaPipe databins, webhook, data collection, lead capture, automation, workflows'
            },
            'Databins list page': {
                title: 'Databins List Page | NinjaPipe',
                description: 'Databins list at /databins: all databins in the workspace. Search, create, open, edit, delete, clear requests.',
                keywords: 'NinjaPipe databins list, manage databins'
            },
            'Creating and editing a databin': {
                title: 'Creating and Editing a Databin | NinjaPipe',
                description: 'Create or edit a databin: name, description, tags. Get a unique webhook URL to receive POST requests.',
                keywords: 'NinjaPipe create databin, databin name, webhook URL'
            },
            'Databin details and requests': {
                title: 'Databin Details and Requests | NinjaPipe',
                description: 'View databin requests: payload, headers, timestamp, status. Copy URL, export data, clear requests, delete databin.',
                keywords: 'NinjaPipe databin requests, webhook payload, export databin'
            },
            'Databin webhook signing': {
                title: 'Databin Webhook Signing | NinjaPipe',
                description: 'Require HMAC signatures on databin webhooks. Enable signing secrets, compute X-Webhook-Signature per request, and integrate from Node.js or Postman.',
                keywords: 'NinjaPipe databin webhook signing, HMAC, X-Webhook-Signature, whsec, webhook security'
            },
            'How databins connect to other features': {
                title: 'How Databins Connect to Other Features | NinjaPipe',
                description: 'Use databins with Workflows (trigger on request), Lead Campaigns (events databin), forms, landing pages, and the Setup Assistant.',
                keywords: 'NinjaPipe databin workflow, databin campaign, lead capture, automation'
            },
            'Forms Overview': {
                title: 'Forms Overview - Build and Collect Data | NinjaPipe',
                description: 'Overview of NinjaPipe forms: drag-and-drop form builder, field types, conditional logic, sharing, embedding, and workflow triggers.',
                keywords: 'NinjaPipe forms, form builder, lead capture, contact form, survey, workflow'
            },
            'Forms list page': {
                title: 'Forms List Page | NinjaPipe',
                description: 'Forms list at /forms: all forms in the workspace. Create, edit, design, share, view responses, export.',
                keywords: 'NinjaPipe forms list, manage forms'
            },
            'Creating and editing a form': {
                title: 'Creating and Editing a Form | NinjaPipe',
                description: 'Create or edit a form: name, description, slug, status. Settings: notifications, thank you message, redirect, spam protection.',
                keywords: 'NinjaPipe create form, form settings, form slug'
            },
            'Form designer overview': {
                title: 'Form Designer Overview | NinjaPipe',
                description: 'Form designer: drag-and-drop canvas, field palette, field settings, conditional logic, save, preview, share.',
                keywords: 'NinjaPipe form designer, form builder'
            },
            'Form fields and field types': {
                title: 'Form Fields and Field Types | NinjaPipe',
                description: 'All form field types: text, email, phone, dropdown, radio, date, file upload, rating, NPS, divider, heading. Required, options, conditional logic.',
                keywords: 'NinjaPipe form fields, form field types, conditional logic'
            },
            'Form settings and appearance': {
                title: 'Form Settings and Appearance | NinjaPipe',
                description: 'Form theme (light/dark/toggle), thank you message, redirect URL, progress bar, form display options.',
                keywords: 'NinjaPipe form settings, form theme, form appearance'
            },
            'Sharing and embedding forms': {
                title: 'Sharing and Embedding Forms | NinjaPipe',
                description: 'Form URL (workspace + slug), copy link, embed iframe, share to social (Twitter, Facebook, LinkedIn, email, WhatsApp).',
                keywords: 'NinjaPipe share form, embed form, form iframe'
            },
            'Form responses': {
                title: 'Form Responses | NinjaPipe',
                description: 'View form submissions, response detail, export responses, download files. Form Submitted workflow trigger.',
                keywords: 'NinjaPipe form responses, form submissions, export responses'
            },
            'Forms and workflows': {
                title: 'Forms and Workflows | NinjaPipe',
                description: 'Trigger workflows when a form is submitted. Use submission data in steps: create contact, send email, update deal.',
                keywords: 'NinjaPipe form workflow, form_submitted trigger, automation'
            },
            'Automations Overview': {
                title: 'Automations Overview - Workflow Automation | NinjaPipe',
                description: 'Overview of NinjaPipe automations: triggers, actions, conditions, variables. Automate contacts, deals, forms, tasks, and more.',
                keywords: 'NinjaPipe automations, workflow, trigger, action, automation'
            },
            'Automations list page': {
                title: 'Automations List Page | NinjaPipe',
                description: 'Automations list at /automations: create, open designer, rename, activate/pause, duplicate, delete. Filter, sort, run counts.',
                keywords: 'NinjaPipe automations list, workflow list'
            },
            'Creating an automation': {
                title: 'Creating an Automation | NinjaPipe',
                description: 'Create a new automation: title and description. Opens workflow designer to add trigger and steps.',
                keywords: 'NinjaPipe create automation, create workflow'
            },
            'Workflow designer overview': {
                title: 'Workflow Designer Overview | NinjaPipe',
                description: 'Workflow designer: canvas, trigger, steps (actions, conditions, wait). Save, activate, test runs.',
                keywords: 'NinjaPipe workflow designer, automation builder'
            },
            'Triggers': {
                title: 'Triggers | NinjaPipe',
                description: 'Workflow triggers: contact created/updated, form submitted, databin received, deal/company/task/invoice/pipeline events.',
                keywords: 'NinjaPipe workflow triggers, form_submitted, databin_received'
            },
            'Actions and steps': {
                title: 'Actions and Steps | NinjaPipe',
                description: 'Workflow actions: send email, create/update contact, create deal, add to list, wait, create task, create invoice, Slack/Discord.',
                keywords: 'NinjaPipe workflow actions, send email, create contact'
            },
            'Conditions and branching': {
                title: 'Conditions and Branching | NinjaPipe',
                description: 'Workflow conditions: equals, contains, greater than, is empty. Contact/deal/task filters. True/False branches.',
                keywords: 'NinjaPipe workflow conditions, branching, if else'
            },
            'Variables and data': {
                title: 'Variables and Data | NinjaPipe',
                description: 'Use variables in workflows: @contact, @deal, @form, @databin, @step. Trigger context and step outputs.',
                keywords: 'NinjaPipe workflow variables, dynamic variables'
            },
            'Running automations and executions': {
                title: 'Running Automations and Executions | NinjaPipe',
                description: 'Activate and pause automations. View run count, last run, execution history, and errors.',
                keywords: 'NinjaPipe workflow execution, workflow runs, activate automation'
            }
        };

        const pageMeta = metadata[pageName] || {
            title: 'API Documentation - Complete Business Integration Guide | NinjaPipe API',
            description: 'Master the NinjaPipe CRM API with comprehensive documentation for customer relationship management, sales pipeline, product catalog, and financial planning. Complete developer guide for business automation.',
            keywords: 'NinjaPipe CRM API, CRM integration, customer relationship management, business automation, sales pipeline, product catalog, financial planning, business tools, API documentation, developer guide, REST API, business CRM, enterprise software'
        };

        return {
            title: pageMeta.title,
            description: pageMeta.description,
            keywords: pageMeta.keywords,
            url: currentUrl,
            canonical: currentUrl
        };
    }, [getUrlFromPage]);

    // Match a doc page by label or by its metadata (title, description, keywords) for search
    const pageMatchesSearch = useCallback((pageName, query) => {
        if (!query?.trim()) return false;
        const q = query.trim().toLowerCase();
        if (pageName.toLowerCase().includes(q)) return true;
        const meta = getPageMetadata(pageName);
        const searchable = [meta.title, meta.description, meta.keywords].filter(Boolean).join(' ').toLowerCase();
        return searchable.includes(q);
    }, [getPageMetadata]);

    // Search functionality - filter navigation items
    const handleSearch = (query) => {
        setSearchQuery(query);
        
        // Auto-expand sections that have matching results
        if (query.trim()) {
            const lowerQuery = query.toLowerCase();
            
            // Check if each section has matching results
            const hasGettingStartedResults = (gettingStartedItems || []).some(item => 
                item.toLowerCase().includes(lowerQuery)
            );
            const hasIntegrationsResults = (integrationsItems || []).some(item => 
                item.toLowerCase().includes(lowerQuery)
            );
            
            if (hasGettingStartedResults) setGettingStartedExpanded(true);
            setGroupExpanded(prev => {
                const next = { ...prev };
                docSidebar.forEach(node => {
                    if (node.type === 'group' && (
                        node.label.toLowerCase().includes(lowerQuery) ||
                        node.chapters.some(ch =>
                            ch.label.toLowerCase().includes(lowerQuery) ||
                            ch.items.some(item => item.toLowerCase().includes(lowerQuery) || pageMatchesSearch(item, query))
                        )
                    )) next[node.label] = true;
                });
                return next;
            });
            setChapterExpanded(prev => {
                const next = { ...prev };
                docSidebar.forEach(node => {
                    if (node.type === 'group') {
                        node.chapters.forEach(ch => {
                            if (ch.label.toLowerCase().includes(lowerQuery) ||
                                ch.items.some(item => item.toLowerCase().includes(lowerQuery) || pageMatchesSearch(item, query))) {
                                next[getChapterKey(node.label, ch.label)] = true;
                            }
                        });
                    } else if (node.type === 'chapter' && (
                        node.label.toLowerCase().includes(lowerQuery) ||
                        node.items.some(item => item.toLowerCase().includes(lowerQuery) || pageMatchesSearch(item, query))
                    )) {
                        next[getChapterKey(null, node.label)] = true;
                    }
                });
                return next;
            });
            if (hasIntegrationsResults) setIntegrationsExpanded(true);
        }
    };


    const getFilteredGettingStartedItems = () => {
        if (!searchQuery.trim()) return gettingStartedItems || [];
        return (gettingStartedItems || []).filter(item => 
            item.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const getFilteredDocSidebar = () => {
        if (!searchQuery.trim()) return docSidebar;
        const q = searchQuery.toLowerCase();
        const itemMatches = (item) => item.toLowerCase().includes(q) || pageMatchesSearch(item, searchQuery);
        return docSidebar.map(node => {
            if (node.type === 'group') {
                const groupLabelMatch = node.label.toLowerCase().includes(q);
                const chapters = node.chapters.map(ch => {
                    const chapterLabelMatch = ch.label.toLowerCase().includes(q);
                    const matchingItems = ch.items.filter(itemMatches);
                    const items = groupLabelMatch || chapterLabelMatch ? ch.items : matchingItems;
                    if (items.length === 0 && !chapterLabelMatch && !groupLabelMatch) return null;
                    return { ...ch, items };
                }).filter(Boolean);
                if (chapters.length === 0) return null;
                return { ...node, chapters };
            }
            // Standalone chapter
            const match = node.label.toLowerCase().includes(q) || node.items.some(itemMatches);
            if (!match) return null;
            const matchingItems = node.items.filter(itemMatches);
            const items = node.label.toLowerCase().includes(q) ? node.items : matchingItems;
            return { ...node, items };
        }).filter(Boolean);
    };

    const getFilteredIntegrationsItems = () => {
        if (!searchQuery.trim()) return integrationsItems || [];
        return (integrationsItems || []).filter(item => 
            item.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };


    // Helper function to highlight matching text
    const highlightText = (text, query) => {
        if (!query.trim()) return text;
        
        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);
        
        return parts.map((part, index) => {
            if (regex.test(part)) {
                return (
                    <span key={index} style={{
                        backgroundColor: '#10B981',
                        color: 'var(--text-primary)',
                        padding: '1px 2px',
                        borderRadius: '3px',
                        fontWeight: 600
                    }}>
                        {part}
                    </span>
                );
            }
            return part;
        });
    };

    // Quick Links (Table of Contents) component
    const QuickLinks = () => {
        const [headings, setHeadings] = useState([]);
        const quickLinksRef = useRef(null);

        useEffect(() => {
            // Find the content area
            const contentArea = document.querySelector('[data-content-area]');
            if (!contentArea) return;

            // Find all h2 and h3 elements
            const headingElements = contentArea.querySelectorAll('h2, h3');
            const headingsList = [];

            headingElements.forEach((heading, index) => {
                // Generate ID if it doesn't have one
                let id = heading.id;
                if (!id) {
                    // Create ID from text content
                    id = heading.textContent
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-+|-+$/g, '') || `heading-${index}`;
                    heading.id = id;
                }

                headingsList.push({
                    id,
                    text: heading.textContent,
                    level: heading.tagName.toLowerCase()
                });
            });

            setHeadings(headingsList);
        }, []); // Run once on mount, content updates trigger re-render

        const scrollToHeading = (id) => {
            const element = document.getElementById(id);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        };

        if (headings.length === 0) {
            return null;
        }

        return (
            <div 
                ref={quickLinksRef}
                style={{
                    maxWidth: 200,
                    maxHeight: 'calc(100vh - 280px)',
                    overflowY: 'auto',
                    backgroundColor: 'var(--bg-primary)',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-primary)'
                }}
            >
                <div style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    marginBottom: 12,
                    paddingBottom: 8,
                    borderBottom: '1px solid var(--border-primary)'
                }}>
                    Quick Links
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4
                }}>
                    {headings.map((heading, index) => (
                        <button
                            key={index}
                            onClick={() => scrollToHeading(heading.id)}
                            style={{
                                textAlign: 'left',
                                padding: '6px 8px',
                                fontSize: heading.level === 'h2' ? 13 : 12,
                                fontWeight: heading.level === 'h2' ? 600 : 400,
                                color: 'var(--text-secondary)',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                transition: 'all 120ms ease',
                                paddingLeft: heading.level === 'h3' ? 16 : 8,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                e.currentTarget.style.color = 'var(--brand-cyan)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = 'var(--text-secondary)';
                            }}
                            title={heading.text}
                        >
                            {heading.text}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    // Share buttons component
    const ShareButtons = ({ location }) => {
        const getShareUrl = () => {
            const baseUrl = 'https://docs.ninjapipe.app';
            return `${baseUrl}${location.pathname}`;
        };

        const getShareTitle = () => {
            return document.title || 'NinjaPipe Documentation';
        };

        const handleShare = (platform) => {
            const url = getShareUrl();
            const title = getShareTitle();
            const text = `Check out this documentation: ${title}`;

            switch (platform) {
                case 'twitter':
                    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
                    break;
                case 'linkedin':
                    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
                    break;
                case 'facebook':
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                    break;
                case 'copy':
                    navigator.clipboard.writeText(url);
                    toast.success('Link copied to clipboard!');
                    break;
                default:
                    break;
            }
        };

        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                alignItems: 'flex-start'
            }}>
                <div style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    marginBottom: 8
                }}>
                    Share
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 8,
                    alignItems: 'center'
                }}>
                    <button
                        onClick={() => handleShare('twitter')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: '6px',
                            border: '1px solid var(--border-primary)',
                            backgroundColor: 'transparent',
                            color: 'var(--text-secondary)',
                            cursor: 'pointer',
                            transition: 'all 120ms ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                            e.currentTarget.style.color = 'var(--brand-cyan)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                        title="Share on X (Twitter)"
                    >
                        <FaXTwitter style={{ fontSize: 14 }} />
                    </button>
                    <button
                        onClick={() => handleShare('linkedin')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: '6px',
                            border: '1px solid var(--border-primary)',
                            backgroundColor: 'transparent',
                            color: 'var(--text-secondary)',
                            cursor: 'pointer',
                            transition: 'all 120ms ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                            e.currentTarget.style.color = '#0077b5';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                        title="Share on LinkedIn"
                    >
                        <FaLinkedin style={{ fontSize: 14 }} />
                    </button>
                    <button
                        onClick={() => handleShare('facebook')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: '6px',
                            border: '1px solid var(--border-primary)',
                            backgroundColor: 'transparent',
                            color: 'var(--text-secondary)',
                            cursor: 'pointer',
                            transition: 'all 120ms ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                            e.currentTarget.style.color = '#1877f2';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                        title="Share on Facebook"
                    >
                        <FaFacebook style={{ fontSize: 14 }} />
                    </button>
                    <button
                        onClick={() => handleShare('copy')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: '6px',
                            border: '1px solid var(--border-primary)',
                            backgroundColor: 'transparent',
                            color: 'var(--text-secondary)',
                            cursor: 'pointer',
                            transition: 'all 120ms ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                            e.currentTarget.style.color = 'var(--brand-cyan)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                        title="Copy link"
                    >
                        <FaCopy style={{ fontSize: 14 }} />
                    </button>
                </div>
            </div>
        );
    };

    // Author section component
    const AuthorSection = ({ location }) => {
        // Calculate date 7 days ago from today
        const getLastUpdatedDate = () => {
            const today = new Date();
            const sevenDaysAgo = new Date(today);
            sevenDaysAgo.setDate(today.getDate() - 7);
            
            // Format as "Month Day, Year" (e.g., "February 12, 2026")
            const options = { year: 'numeric', month: 'long', day: 'numeric' };
            return sevenDaysAgo.toLocaleDateString('en-US', options);
        };

        return (
            <div style={{
                marginTop: 48,
                paddingTop: 32,
                borderTop: '1px solid var(--border-primary)'
            }}>
                {/* Last updated section */}
                <div style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    marginBottom: 16
                }}>
                    Last updated on {getLastUpdatedDate()}
                </div>
                
                <div style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    marginBottom: 16
                }}>
                    Written by
                </div>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16
                }}>
                <img
                    src="https://mdfzsakzfudassdmiedx.supabase.co/storage/v1/object/public/avatars/avatars/68a35a55-4bfb-43e2-bb01-8367665a4d36.png"
                    alt="Dinuka Jay"
                    style={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid var(--border-primary)'
                    }}
                />
                <div style={{
                    display: 'flex',
                    gap: 24,
                    alignItems: 'center'
                }}>
                    {/* Profile Info */}
                    <div>
                        <div style={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            marginBottom: 4
                        }}>
                            Dinuka Jay
                        </div>
                        <div style={{
                            fontSize: 14,
                            color: 'var(--text-secondary)',
                            marginBottom: 2
                        }}>
                            Founder, NinjaPipe
                        </div>
                        <div style={{
                            fontSize: 12,
                            color: 'var(--text-secondary)',
                            opacity: 0.8
                        }}>
                            dinuka@ninjapipe.com
                        </div>
                    </div>

                    {/* Socials Section */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        paddingLeft: 24,
                        borderLeft: '1px solid var(--border-primary)'
                    }}>
                        <div style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--text-secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            marginBottom: 4
                        }}>
                            Connect with me
                        </div>
                        <a
                            href="https://www.instagram.com/dinuka_jay/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                color: 'var(--text-secondary)',
                                textDecoration: 'none',
                                fontSize: 14,
                                transition: 'color 120ms ease',
                                marginBottom: 8
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = 'var(--brand-cyan)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = 'var(--text-secondary)';
                            }}
                        >
                            <FaInstagram style={{ fontSize: 16 }} />
                            <span>dinuka_jay</span>
                        </a>
                        <a
                            href="https://x.com/dinuka_jay"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                color: 'var(--text-secondary)',
                                textDecoration: 'none',
                                fontSize: 14,
                                transition: 'color 120ms ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = 'var(--brand-cyan)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = 'var(--text-secondary)';
                            }}
                        >
                            <FaXTwitter style={{ fontSize: 16 }} />
                            <span>dinuka_jay</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
        );
    };

    const renderContent = useMemo(() => {
        // Try Getting Started category first
        const gettingStartedContent = getGettingStartedContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (gettingStartedContent) return gettingStartedContent;

        // Try Contacts category
        const contactsContent = getContactsContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (contactsContent) return contactsContent;

        // Try Client Portals category
        const clientPortalsContent = getClientPortalsContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (clientPortalsContent) return clientPortalsContent;

        // Try Inbox category
        const inboxContent = getInboxContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (inboxContent) return inboxContent;

        // Try Email Campaigns, then Email Designs
        const emailCampaignsContent = getEmailCampaignsContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (emailCampaignsContent) return emailCampaignsContent;
        const emailDesignsContent = getEmailDesignsContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (emailDesignsContent) return emailDesignsContent;

        // Try Workspaces category
        const workspacesContent = getWorkspacesContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (workspacesContent) return workspacesContent;

        // Try Manage Account category (Account details, Integrations, Security, Licenses, Billing & Subscription)
        const manageAccountContent = getManageAccountContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (manageAccountContent) return manageAccountContent;

        // Try Pipelines category
        const pipelinesContent = getPipelinesContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (pipelinesContent) return pipelinesContent;
        const invoicesContent = getInvoicesContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (invoicesContent) return invoicesContent;
        const bookingPagesContent = getBookingPagesContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (bookingPagesContent) return bookingPagesContent;
        const yourDayContent = getYourDayContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (yourDayContent) return yourDayContent;
        const dashboardContent = getDashboardContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (dashboardContent) return dashboardContent;
        const taskManagementContent = getTaskManagementContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (taskManagementContent) return taskManagementContent;
        const whiteboardsContent = getWhiteboardsContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (whiteboardsContent) return whiteboardsContent;
        const databinsContent = getDatabinsContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (databinsContent) return databinsContent;
        const formsContent = getFormsContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (formsContent) return formsContent;
        const automationsContent = getAutomationsContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (automationsContent) return automationsContent;

        // Try Authentication category
        const authContent = getAuthenticationContent(selectedItem, { location, getUrlFromPage, AuthorSection });
        if (authContent) return authContent;

        // Fall back to switch for API docs and other cases
        switch (selectedItem) {
            case 'Pagination':
                return {
                    title: 'Pagination',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                All GET endpoints that return collections support pagination to help you efficiently retrieve large datasets. 
                                Pagination is consistent across all list endpoints in the NinjaPipe API.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Use the <code style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: `1px solid var(--border-primary)`,
                                    padding: '2px 8px',
                                    borderRadius: 8,
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    fontSize: 14,
                                    color: 'var(--text-primary)'
                                }}>page</code> and <code style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: `1px solid var(--border-primary)`,
                                    padding: '2px 8px',
                                    borderRadius: 8,
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    fontSize: 14,
                                    color: 'var(--text-primary)'
                                }}>limit</code> query parameters to control which results you receive.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The response includes a <code style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: `1px solid var(--border-primary)`,
                                    padding: '2px 8px',
                                    borderRadius: 8,
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    fontSize: 14,
                                    color: 'var(--text-primary)'
                                }}>pagination</code> object with metadata about the current page and total results.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Pagination Parameters',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number to retrieve (starts from 1)' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Number of items per page (maximum 100)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/contacts?page=2&limit=10" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "first_name": "John",
      "last_name": "Doe",
      "email": "john.doe@example.com",
      "status": "Active",
      "created_at": "2024-01-15T10:30:00Z"
    }
  ],
  "pagination": {
    "page": 2,
    "limit": 10,
    "total": 150,
    "pages": 15
  },
  "meta": {
    "workspace_id": "ws_123",
    "filters": {},
    "sort": {
      "field": "created_at",
      "order": "desc"
    }
  }
}`
                        },
                        {
                            title: 'Pagination Response Fields',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: true, description: 'Current page number' },
                                { name: 'limit', type: 'integer', required: true, description: 'Number of items per page' },
                                { name: 'total', type: 'integer', required: true, description: 'Total number of items across all pages' },
                                { name: 'pages', type: 'integer', required: true, description: 'Total number of pages available' }
                            ]
                        }
                    ]
                };

            case 'List Contacts':
                return {
      title: 'List Contacts',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a paginated list of contacts with optional filtering and search capabilities.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                You can filter contacts by status, owner, and search across name, email, and phone fields. 
                                Results are paginated with a maximum of 100 contacts per page.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Parameters',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number for pagination' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Number of contacts per page (max 100)' },
        { name: 'search', type: 'string', required: false, description: 'Search in name, email, and phone fields' },
        { name: 'status', type: 'string', required: false, description: 'Filter by contact status' },
                                { name: 'owner_id', type: 'string', required: false, description: 'Filter by contact owner' },
                                { name: 'sort_by', type: 'string', required: false, default: 'created_at', description: 'Field to sort by' },
                                { name: 'sort_order', type: 'string', required: false, default: 'desc', description: 'Sort order (asc/desc)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/contacts?page=1&limit=20&search=john" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "first_name": "John",
      "last_name": "Doe",
      "email": "john.doe@example.com",
      "phone": "+1234567890",
      "status": "Active",
      "created_at": "2024-01-15T10:30:00Z",
      "updated_at": "2024-01-15T10:30:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 1,
    "pages": 1
  }
}`
                        }
                    ]
                };

            case 'Create Contact':
                return {
      title: 'Create Contact',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a new contact in your workspace. At least one name field (first_name or last_name) is required.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Email addresses must be unique within your workspace. The contact will be assigned to you by default 
                                unless you specify a different owner_id.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
        { name: 'first_name', type: 'string', required: false, description: 'Contact first name' },
        { name: 'last_name', type: 'string', required: false, description: 'Contact last name' },
        { name: 'email', type: 'string', required: false, description: 'Contact email address' },
        { name: 'phone', type: 'string', required: false, description: 'Contact phone number' },
                                { name: 'status', type: 'string', required: false, default: 'Active', description: 'Contact status' },
                                { name: 'owner_id', type: 'string', required: false, description: 'Contact owner ID' },
        { name: 'company_id', type: 'string', required: false, description: 'Associated company ID' },
                                { name: 'fields', type: 'object', required: false, description: 'Custom field data object containing additional contact information' },
                                { name: 'fields.mrr', type: 'string', required: false, description: 'Monthly recurring revenue' },
                                { name: 'fields.city', type: 'string', required: false, description: 'Contact city' },
                                { name: 'fields.tags', type: 'string', required: false, description: 'Contact tags (comma-separated)' },
                                { name: 'fields.churn', type: 'string', required: false, description: 'Churn status' },
                                { name: 'fields.state', type: 'string', required: false, description: 'Contact state or province' },
                                { name: 'fields.tiktok', type: 'string', required: false, description: 'TikTok handle' },
                                { name: 'fields.address', type: 'string', required: false, description: 'Physical address' },
                                { name: 'fields.by_text', type: 'boolean', required: false, description: 'Contact preference: allow text messages' },
                                { name: 'fields.country', type: 'string', required: false, description: 'Contact country' },
                                { name: 'fields.revenue', type: 'string', required: false, description: 'Revenue amount' },
                                { name: 'fields.twitter', type: 'string', required: false, description: 'Twitter handle' },
                                { name: 'fields.website', type: 'string', required: false, description: 'Website URL' },
                                { name: 'fields.by_email', type: 'boolean', required: false, description: 'Contact preference: allow emails' },
                                { name: 'fields.by_phone', type: 'boolean', required: false, description: 'Contact preference: allow phone calls' },
                                { name: 'fields.category', type: 'string', required: false, description: 'Contact category' },
                                { name: 'fields.currency', type: 'string', required: false, description: 'Currency code (e.g., USD, EUR)' },
                                { name: 'fields.facebook', type: 'string', required: false, description: 'Facebook page URL' },
                                { name: 'fields.industry', type: 'string', required: false, description: 'Industry type' },
                                { name: 'fields.linkedin', type: 'string', required: false, description: 'LinkedIn profile URL' },
                                { name: 'fields.position', type: 'string', required: false, description: 'Job position or title' },
                                { name: 'fields.timezone', type: 'string', required: false, description: 'Timezone (e.g., Europe/Madrid)' },
                                { name: 'fields.whatsapp', type: 'string', required: false, description: 'WhatsApp number' },
                                { name: 'fields.zip_code', type: 'string', required: false, description: 'Postal/ZIP code' },
                                { name: 'fields.by_social', type: 'boolean', required: false, description: 'Contact preference: allow social media outreach' },
                                { name: 'fields.leadSource', type: 'string', required: false, description: 'Lead source information' },
                                { name: 'fields.companySize', type: 'string', required: false, description: 'Company size range' },
                                { name: 'fields.jobFunction', type: 'string', required: false, description: 'Job function or department' },
                                { name: 'fields.social_tiktok', type: 'string', required: false, description: 'TikTok profile URL' },
                                { name: 'fields.social_twitter', type: 'string', required: false, description: 'Twitter profile URL' },
                                { name: 'fields.social_facebook', type: 'string', required: false, description: 'Facebook profile URL' },
                                { name: 'fields.social_linkedin', type: 'string', required: false, description: 'LinkedIn profile URL' },
                                { name: 'fields.social_whatsapp', type: 'string', required: false, description: 'WhatsApp profile URL' },
                                { name: 'fields.social_instagram', type: 'string', required: false, description: 'Instagram profile URL' },
                                { name: 'fields.name_value_pairs', type: 'array', required: false, description: 'Array of custom name-value pairs' },
                                { name: 'fields.additional_details', type: 'string', required: false, description: 'Additional notes or details about the contact' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/contacts" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
  "first_name": "Lucia",
  "last_name": "Martinez",
  "email": "lucia.martinez@solarluna.co",
  "phone": "+34 612 458 937",
  "status": "Qualified",
  "fields": {
    "mrr": "3200",
    "city": "Barcelona",
    "tags": "renewable, solar, europe",
    "churn": "false",
    "state": "Catalonia",
    "tiktok": "@luciasolarluna",
    "address": "Carrer de Casp 45, 3rd Floor",
    "by_text": true,
    "country": "Spain",
    "revenue": "38500",
    "twitter": "@luciamartinez_es",
    "website": "https://solarluna.co",
    "by_email": true,
    "by_phone": false,
    "category": "Renewable Energy",
    "currency": "EUR",
    "facebook": "facebook.com/solarluna.co",
    "industry": "Clean Technology",
    "linkedin": "linkedin.com/in/luciamartinezsolar",
    "position": "Head of Partnerships",
    "timezone": "Europe/Madrid",
    "whatsapp": "+34 612 458 937",
    "zip_code": "08010",
    "by_social": true,
    "leadSource": "Inbound - Company Website",
    "companySize": "51-200",
    "jobFunction": "Business Development",
    "social_tiktok": "tiktok.com/@luciasolarluna",
    "social_twitter": "twitter.com/luciamartinez_es",
    "social_facebook": "facebook.com/solarluna.co",
    "social_linkedin": "linkedin.com/in/luciamartinezsolar",
    "social_whatsapp": "wa.me/34612458937",
    "name_value_pairs": [
      { "name": "Lead Temperature", "value": "Hot" },
      { "name": "Meeting Scheduled", "value": "2025-11-02" }
    ],
    "social_instagram": "instagram.com/solarluna.energy",
    "additional_details": "Lucia is exploring potential distributors in Southern Europe. Prefers morning calls between 9–11 AM local time."
  }
}'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "first_name": "Lucia",
    "last_name": "Martinez",
    "email": "lucia.martinez@solarluna.co",
    "phone": "+34 612 458 937",
    "status": "Qualified",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z",
    "fields": {
      "mrr": "3200",
      "city": "Barcelona",
      "tags": "renewable, solar, europe",
      "churn": "false",
      "state": "Catalonia",
      "tiktok": "@luciasolarluna",
      "address": "Carrer de Casp 45, 3rd Floor",
      "by_text": true,
      "country": "Spain",
      "revenue": "38500",
      "twitter": "@luciamartinez_es",
      "website": "https://solarluna.co",
      "by_email": true,
      "by_phone": false,
      "category": "Renewable Energy",
      "currency": "EUR",
      "facebook": "facebook.com/solarluna.co",
      "industry": "Clean Technology",
      "linkedin": "linkedin.com/in/luciamartinezsolar",
      "position": "Head of Partnerships",
      "timezone": "Europe/Madrid",
      "whatsapp": "+34 612 458 937",
      "zip_code": "08010",
      "by_social": true,
      "leadSource": "Inbound - Company Website",
      "companySize": "51-200",
      "jobFunction": "Business Development",
      "social_tiktok": "tiktok.com/@luciasolarluna",
      "social_twitter": "twitter.com/luciamartinez_es",
      "social_facebook": "facebook.com/solarluna.co",
      "social_linkedin": "linkedin.com/in/luciamartinezsolar",
      "social_whatsapp": "wa.me/34612458937",
      "name_value_pairs": [
        { "name": "Lead Temperature", "value": "Hot" },
        { "name": "Meeting Scheduled", "value": "2025-11-02" }
      ],
      "social_instagram": "instagram.com/solarluna.energy",
      "additional_details": "Lucia is exploring potential distributors in Southern Europe. Prefers morning calls between 9–11 AM local time."
    }
  },
  "message": "Contact created successfully"
}`
                        }
                    ]
                };

            case 'Get Contact':
                return {
                    title: 'Get Contact',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a specific contact by its ID. The contact ID must be a valid UUID format.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The response includes the contact's details along with associated owner and company information.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The contact ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/contacts/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "first_name": "John",
    "last_name": "Doe",
    "email": "john.doe@example.com",
    "phone": "+1234567890",
    "status": "Active",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z",
    "accounts": {
      "id": "acc_123",
    "first_name": "Jane",
    "last_name": "Smith",
      "email": "jane@company.com"
    },
    "companies": {
      "id": "comp_456",
      "name": "Acme Corp",
      "website": "https://acme.com",
      "industry": "Technology"
    }
  }
}`
                        }
                    ]
                };

            case 'Update Contact':
                return {
                    title: 'Update Contact',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Update an existing contact. You can update any field by including it in the request body.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                If updating the email address, it must be unique within your workspace. Only provided fields will be updated.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The contact ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'first_name', type: 'string', required: false, description: 'Updated first name' },
                                { name: 'last_name', type: 'string', required: false, description: 'Updated last name' },
                                { name: 'email', type: 'string', required: false, description: 'Updated email address' },
                                { name: 'phone', type: 'string', required: false, description: 'Updated phone number' },
                                { name: 'status', type: 'string', required: false, description: 'Updated status' },
                                { name: 'owner_id', type: 'string', required: false, description: 'Updated owner ID' },
                                { name: 'company_id', type: 'string', required: false, description: 'Updated company ID' },
                                { name: 'fields', type: 'object', required: false, description: 'Updated custom fields' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/contacts/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "first_name": "Jane",
    "last_name": "Smith",
    "email": "jane.smith@example.com",
    "status": "Active"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "first_name": "Jane",
    "last_name": "Smith",
    "email": "jane.smith@example.com",
    "phone": "+1234567890",
    "status": "Active",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T11:45:00Z"
  },
  "message": "Contact updated successfully"
}`
                        }
                    ]
                };

            case 'Delete Contact':
                return {
                    title: 'Delete Contact',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Permanently delete a contact from your workspace. This action cannot be undone.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The contact must exist and belong to your workspace. You will receive confirmation of the deletion.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The contact ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/contacts/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "message": "Contact deleted successfully",
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "John Doe"
  }
}`
                        }
                    ]
                };

            case 'List Deals':
                return {
                    title: 'List Deals',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a paginated list of deals with optional filtering and search capabilities.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                You can filter deals by status, owner, and search across name fields. 
                                Results are paginated with a maximum of 100 deals per page.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Parameters',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number for pagination' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Number of deals per page (max 100)' },
                                { name: 'search', type: 'string', required: false, description: 'Search deals by name' },
                                { name: 'status', type: 'string', required: false, description: 'Filter by deal status (Open, Won, Lost, In Progress)' },
                                { name: 'owner_id', type: 'string', required: false, description: 'Filter by deal owner ID' },
                                { name: 'sort_by', type: 'string', required: false, default: 'created_at', description: 'Sort field (created_at, updated_at, name, value, status)' },
                                { name: 'sort_order', type: 'string', required: false, default: 'desc', description: 'Sort order (asc, desc)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/deals?page=1&limit=20&status=Open&sort_by=value&sort_order=desc" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "name": "Enterprise Software License",
      "value": 50000,
      "status": "Open",
      "owner_id": "456e7890-e89b-12d3-a456-426614174001",
      "created_by": "456e7890-e89b-12d3-a456-426614174001",
      "contact_id": "789e0123-e89b-12d3-a456-426614174002",
      "company_id": "012e3456-e89b-12d3-a456-426614174003",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
      "fields": {},
      "created_at": "2024-01-15T10:30:00Z",
      "updated_at": "2024-01-15T10:30:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 1,
    "pages": 1
  },
  "meta": {
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "filters": {
      "search": null,
      "status": "Open",
      "owner_id": null
    },
    "sort": {
      "field": "value",
      "order": "desc"
    }
  }
}`
                        }
                    ]
                };

            case 'Create Deal':
                return {
                    title: 'Create Deal',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a new deal in your workspace. Deals represent sales opportunities with associated contacts and companies.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The deal name is required. You can optionally set the value, status, and associate it with contacts and companies.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: true, description: 'Deal name' },
                                { name: 'value', type: 'number', required: false, default: '0', description: 'Deal value' },
                                { name: 'status', type: 'string', required: false, default: 'Open', description: 'Deal status (Open, Won, Lost, In Progress)' },
                                { name: 'owner_id', type: 'string', required: false, description: 'Deal owner ID (defaults to API key owner)' },
                                { name: 'contact_id', type: 'string', required: false, description: 'Associated contact ID' },
                                { name: 'company_id', type: 'string', required: false, description: 'Associated company ID' },
                                { name: 'fields', type: 'object', required: false, default: '{}', description: 'Custom fields as key-value pairs' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/deals" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Enterprise Software License",
    "value": 50000,
    "status": "Open",
    "contact_id": "789e0123-e89b-12d3-a456-426614174002",
    "company_id": "012e3456-e89b-12d3-a456-426614174003"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Enterprise Software License",
    "value": 50000,
    "status": "Open",
    "priority": "High",
    "description": "Annual enterprise software license renewal",
    "expected_close_date": "2024-12-31",
    "owner_id": "456e7890-e89b-12d3-a456-426614174001",
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "contact_id": "789e0123-e89b-12d3-a456-426614174002",
    "company_id": "012e3456-e89b-12d3-a456-426614174003",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "fields": {},
    "slug": "enterprise-software-license",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z"
  },
  "message": "Deal created successfully"
}`
                        }
                    ]
                };

            case 'Get Deal':
                return {
                    title: 'Get Deal',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a specific deal by its ID. Returns complete deal information including associated contact and company details.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The deal must exist and belong to your workspace. Returns 404 if the deal is not found.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The deal ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/deals/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Enterprise Software License",
    "value": 50000,
    "status": "Open",
    "priority": "High",
    "description": "Annual enterprise software license renewal",
    "expected_close_date": "2024-12-31",
    "owner_id": "456e7890-e89b-12d3-a456-426614174001",
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "contact_id": "789e0123-e89b-12d3-a456-426614174002",
    "company_id": "012e3456-e89b-12d3-a456-426614174003",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "fields": {},
    "slug": "enterprise-software-license",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z",
    "accounts": {
      "id": "456e7890-e89b-12d3-a456-426614174001",
      "first_name": "John",
      "last_name": "Smith",
      "email": "john.smith@company.com"
    },
    "contacts": {
      "id": "789e0123-e89b-12d3-a456-426614174002",
      "first_name": "Jane",
      "last_name": "Doe",
      "email": "jane.doe@company.com",
      "phone": "+1234567890"
    },
    "companies": {
      "id": "012e3456-e89b-12d3-a456-426614174003",
      "name": "Acme Corporation",
      "website": "https://acme.com",
      "industry": "Technology"
    }
  }
}`
                        }
                    ]
                };

            case 'Update Deal':
                return {
                    title: 'Update Deal',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Update an existing deal. You can modify any deal field including name, value, status, priority, and associated contacts or companies.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Only provided fields will be updated. The deal must exist and belong to your workspace.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The deal ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: false, description: 'Deal name' },
                                { name: 'value', type: 'number', required: false, description: 'Deal value in cents' },
                                { name: 'status', type: 'string', required: false, description: 'Deal status (Open, Won, Lost, In Progress)' },
                                { name: 'priority', type: 'string', required: false, description: 'Deal priority (Low, Medium, High)' },
                                { name: 'description', type: 'string', required: false, description: 'Deal description' },
                                { name: 'expected_close_date', type: 'string', required: false, description: 'Expected close date (YYYY-MM-DD format)' },
                                { name: 'owner_id', type: 'string', required: false, description: 'Deal owner ID' },
                                { name: 'contact_id', type: 'string', required: false, description: 'Associated contact ID' },
                                { name: 'company_id', type: 'string', required: false, description: 'Associated company ID' },
                                { name: 'fields', type: 'object', required: false, description: 'Custom fields as key-value pairs' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/deals/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "status": "Won",
    "value": 55000,
    "description": "Deal closed successfully with additional services"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Enterprise Software License",
    "value": 55000,
    "status": "Won",
    "priority": "High",
    "description": "Deal closed successfully with additional services",
    "expected_close_date": "2024-12-31",
    "owner_id": "456e7890-e89b-12d3-a456-426614174001",
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "contact_id": "789e0123-e89b-12d3-a456-426614174002",
    "company_id": "012e3456-e89b-12d3-a456-426614174003",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "fields": {},
    "slug": "enterprise-software-license",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T11:45:00Z"
  },
  "message": "Deal updated successfully"
}`
                        }
                    ]
                };

            case 'Delete Deal':
                return {
                    title: 'Delete Deal',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Permanently delete a deal from your workspace. This action cannot be undone.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The deal must exist and belong to your workspace. You will receive confirmation of the deletion.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The deal ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/deals/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "message": "Deal deleted successfully",
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Enterprise Software License"
  }
}`
                        }
                    ]
                };

            case 'List Products':
                return {
                    title: 'List Products',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a paginated list of products with optional filtering and search capabilities.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                You can filter products by status, category, product type, and search across name, description, and SKU fields. 
                                Results are paginated with a maximum of 100 products per page.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Parameters',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number for pagination' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Number of products per page (max 100)' },
                                { name: 'search', type: 'string', required: false, description: 'Search products by name, description, or SKU' },
                                { name: 'status', type: 'string', required: false, description: 'Filter by product status (Active, Draft, Archived, Discontinued)' },
                                { name: 'category', type: 'string', required: false, description: 'Filter by product category' },
                                { name: 'product_type', type: 'string', required: false, description: 'Filter by product type (Product, Service, Digital)' },
                                { name: 'sort_by', type: 'string', required: false, default: 'created_at', description: 'Sort field (created_at, updated_at, name, price, status, category, product_type)' },
                                { name: 'sort_order', type: 'string', required: false, default: 'desc', description: 'Sort order (asc, desc)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/products?page=1&limit=20&status=Active&sort_by=price&sort_order=desc" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "name": "Premium CRM License",
      "description": "Full-featured CRM solution for enterprise businesses",
      "category": "Software",
      "subcategory": "CRM",
      "sku": "CRM-PREM-001",
      "price": 299.00,
      "cost": 150.00,
      "currency": "USD",
      "status": "Active",
      "product_type": "Service",
      "image_url": "https://example.com/product-image.jpg",
      "settings_json": {
        "inventory": {
          "trackInventory": true,
          "quantity": 150,
          "lowStockThreshold": 10,
          "location": "Digital"
        },
        "tags": ["CRM", "Enterprise", "Analytics"],
        "features": ["Unlimited contacts", "Advanced reporting"],
        "featured": true,
        "salesCount": 45,
        "revenue": 13455.00
      },
      "created_by": "456e7890-e89b-12d3-a456-426614174001",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
      "created_at": "2024-01-15T10:30:00Z",
      "updated_at": "2024-01-15T10:30:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 1,
    "pages": 1
  },
  "meta": {
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "filters": {
      "search": null,
      "status": "Active",
      "category": null,
      "product_type": null
    },
    "sort": {
      "field": "price",
      "order": "desc"
    }
  }
}`
                        }
                    ]
                };

            case 'Create Product':
                return {
                    title: 'Create Product',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a new product in your workspace. Products represent items or services in your catalog with pricing and inventory information.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The product name and SKU are required. You can optionally set the price, cost, category, status, and additional settings.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: true, description: 'Product name' },
                                { name: 'description', type: 'string', required: false, description: 'Product description' },
                                { name: 'category', type: 'string', required: false, description: 'Product category' },
                                { name: 'subcategory', type: 'string', required: false, description: 'Product subcategory' },
                                { name: 'sku', type: 'string', required: true, description: 'Product SKU (must be unique in workspace)' },
                                { name: 'price', type: 'number', required: true, description: 'Product price' },
                                { name: 'cost', type: 'number', required: false, default: '0', description: 'Product cost' },
                                { name: 'currency', type: 'string', required: false, default: 'USD', description: 'Currency code' },
                                { name: 'status', type: 'string', required: false, default: 'Active', description: 'Product status (Active, Draft, Archived, Discontinued)' },
                                { name: 'product_type', type: 'string', required: false, default: 'Product', description: 'Product type (Product, Service, Digital)' },
                                { name: 'image_url', type: 'string', required: false, description: 'Product image URL' },
                                { name: 'settings_json', type: 'object', required: false, default: '{}', description: 'Additional product settings (inventory, tags, features, etc.)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/products" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Premium CRM License",
    "description": "Full-featured CRM solution for enterprise businesses",
    "category": "Software",
    "subcategory": "CRM",
    "sku": "CRM-PREM-001",
    "price": 299.00,
    "cost": 150.00,
    "currency": "USD",
    "status": "Active",
    "product_type": "Service",
    "settings_json": {
      "inventory": {
        "trackInventory": true,
        "quantity": 150,
        "lowStockThreshold": 10,
        "location": "Digital"
      },
      "tags": ["CRM", "Enterprise", "Analytics"],
      "features": ["Unlimited contacts", "Advanced reporting"]
    }
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Premium CRM License",
    "description": "Full-featured CRM solution for enterprise businesses",
    "category": "Software",
    "subcategory": "CRM",
    "sku": "CRM-PREM-001",
    "price": 299.00,
    "cost": 150.00,
    "currency": "USD",
    "status": "Active",
    "product_type": "Service",
    "image_url": null,
    "settings_json": {
      "inventory": {
        "trackInventory": true,
        "quantity": 150,
        "lowStockThreshold": 10,
        "location": "Digital"
      },
      "tags": ["CRM", "Enterprise", "Analytics"],
      "features": ["Unlimited contacts", "Advanced reporting"]
    },
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z"
  },
  "message": "Product created successfully"
}`
                        }
                    ]
                };

            case 'Get Product':
                return {
                    title: 'Get Product',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a specific product by its ID. Returns complete product information including settings and metadata.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The product must exist and belong to your workspace. Returns 404 if the product is not found.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The product ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/products/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Premium CRM License",
    "description": "Full-featured CRM solution for enterprise businesses",
    "category": "Software",
    "subcategory": "CRM",
    "sku": "CRM-PREM-001",
    "price": 299.00,
    "cost": 150.00,
    "currency": "USD",
    "status": "Active",
    "product_type": "Service",
    "image_url": "https://example.com/product-image.jpg",
    "settings_json": {
      "inventory": {
        "trackInventory": true,
        "quantity": 150,
        "lowStockThreshold": 10,
        "location": "Digital"
      },
      "tags": ["CRM", "Enterprise", "Analytics"],
      "features": ["Unlimited contacts", "Advanced reporting"],
      "featured": true,
      "salesCount": 45,
      "revenue": 13455.00
    },
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z"
  }
}`
                        }
                    ]
                };

            case 'Update Product':
                return {
                    title: 'Update Product',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Update an existing product. You can modify any product field including name, price, status, category, and settings.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Only provided fields will be updated. The product must exist and belong to your workspace.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The product ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: false, description: 'Product name' },
                                { name: 'description', type: 'string', required: false, description: 'Product description' },
                                { name: 'category', type: 'string', required: false, description: 'Product category' },
                                { name: 'subcategory', type: 'string', required: false, description: 'Product subcategory' },
                                { name: 'sku', type: 'string', required: false, description: 'Product SKU (must be unique in workspace)' },
                                { name: 'price', type: 'number', required: false, description: 'Product price' },
                                { name: 'cost', type: 'number', required: false, description: 'Product cost' },
                                { name: 'currency', type: 'string', required: false, description: 'Currency code' },
                                { name: 'status', type: 'string', required: false, description: 'Product status (Active, Draft, Archived, Discontinued)' },
                                { name: 'product_type', type: 'string', required: false, description: 'Product type (Product, Service, Digital)' },
                                { name: 'image_url', type: 'string', required: false, description: 'Product image URL' },
                                { name: 'settings_json', type: 'object', required: false, description: 'Additional product settings' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/products/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "price": 349.00,
    "status": "Active",
    "settings_json": {
      "featured": true,
      "salesCount": 50
    }
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Premium CRM License",
    "description": "Full-featured CRM solution for enterprise businesses",
    "category": "Software",
    "subcategory": "CRM",
    "sku": "CRM-PREM-001",
    "price": 349.00,
    "cost": 150.00,
    "currency": "USD",
    "status": "Active",
    "product_type": "Service",
    "image_url": null,
    "settings_json": {
      "inventory": {
        "trackInventory": true,
        "quantity": 150,
        "lowStockThreshold": 10,
        "location": "Digital"
      },
      "tags": ["CRM", "Enterprise", "Analytics"],
      "features": ["Unlimited contacts", "Advanced reporting"],
      "featured": true,
      "salesCount": 50,
      "revenue": 13455.00
    },
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T11:45:00Z"
  },
  "message": "Product updated successfully"
}`
                        }
                    ]
                };

            case 'Delete Product':
                return {
                    title: 'Delete Product',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Permanently delete a product from your workspace. This action cannot be undone.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The product must exist and belong to your workspace. You will receive confirmation of the deletion.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The product ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/products/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "message": "Product deleted successfully",
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Premium CRM License"
  }
}`
                        }
                    ]
                };

            case 'List Budgets':
                return {
                    title: 'List Budgets',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a paginated list of budgets with optional filtering and search capabilities.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                You can filter budgets by status, category, period, and search across name and description fields. 
                                Results are paginated with a maximum of 100 budgets per page.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Parameters',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number for pagination' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Number of budgets per page (max 100)' },
                                { name: 'search', type: 'string', required: false, description: 'Search budgets by name or description' },
                                { name: 'status', type: 'string', required: false, description: 'Filter by budget status (Active, Inactive, Completed)' },
                                { name: 'category', type: 'string', required: false, description: 'Filter by budget category (Marketing, Sales, Product, Operations)' },
                                { name: 'period', type: 'string', required: false, description: 'Filter by budget period (Monthly, Quarterly, Yearly)' },
                                { name: 'sort_by', type: 'string', required: false, default: 'created_at', description: 'Sort field (created_at, updated_at, name, allocated_amount, spent_amount, status, category, period)' },
                                { name: 'sort_order', type: 'string', required: false, default: 'desc', description: 'Sort order (asc, desc)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/budgets?page=1&limit=20&status=Active&sort_by=allocated_amount&sort_order=desc" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "name": "Marketing Budget Q1 2024",
      "description": "Q1 marketing budget for digital campaigns, events, and content creation",
      "category": "Marketing",
      "period": "Quarterly",
      "allocated_amount": 50000,
      "spent_amount": 32450,
      "remaining_amount": 17550,
      "currency": "USD",
      "status": "Active",
      "start_date": "2024-01-01",
      "end_date": "2024-03-31",
      "created_by": "456e7890-e89b-12d3-a456-426614174001",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
      "created_at": "2024-01-15T10:30:00Z",
      "updated_at": "2024-01-15T10:30:00Z",
      "accounts": {
        "id": "456e7890-e89b-12d3-a456-426614174001",
        "first_name": "John",
        "last_name": "Smith",
        "email": "john.smith@company.com"
      }
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 1,
    "pages": 1
  },
  "meta": {
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "filters": {
      "search": null,
      "status": "Active",
      "category": null,
      "period": null
    },
    "sort": {
      "field": "allocated",
      "order": "desc"
    }
  }
}`
                        }
                    ]
                };

            case 'Create Budget':
                return {
                    title: 'Create Budget',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a new budget in your workspace. Budgets help track financial planning and spending across different categories and periods.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The budget name and allocated amount are required. You can optionally set the category, period, status, and date range.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                <strong>Note:</strong> The <code>remaining_amount</code> field is automatically calculated by the database as <code>allocated_amount - spent_amount</code> and cannot be manually set.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: true, description: 'Budget name' },
                                { name: 'description', type: 'string', required: false, description: 'Budget description' },
                                { name: 'category', type: 'string', required: false, description: 'Budget category (Marketing, Sales, Product, Operations)' },
                                { name: 'period', type: 'string', required: false, description: 'Budget period (Monthly, Quarterly, Yearly)' },
                                { name: 'allocated', type: 'number', required: true, description: 'Total allocated budget amount (stored as allocated_amount in database)' },
                                { name: 'currency', type: 'string', required: false, default: 'USD', description: 'Currency code' },
                                { name: 'status', type: 'string', required: false, default: 'Active', description: 'Budget status (Active, Inactive, Completed)' },
                                { name: 'start_date', type: 'string', required: false, description: 'Budget start date (YYYY-MM-DD format)' },
                                { name: 'end_date', type: 'string', required: false, description: 'Budget end date (YYYY-MM-DD format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/budgets" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Marketing Budget Q1 2024",
    "description": "Q1 marketing budget for digital campaigns, events, and content creation",
    "category": "Marketing",
    "period": "Quarterly",
    "allocated": 50000,
    "currency": "USD",
    "status": "Active",
    "start_date": "2024-01-01",
    "end_date": "2024-03-31"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Marketing Budget Q1 2024",
    "description": "Q1 marketing budget for digital campaigns, events, and content creation",
    "category": "Marketing",
    "period": "Quarterly",
    "allocated_amount": 50000,
    "spent_amount": 0,
    "remaining_amount": 50000,
    "currency": "USD",
    "status": "Active",
    "start_date": "2024-01-01",
    "end_date": "2024-03-31",
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z"
  },
  "message": "Budget created successfully"
}`
                        }
                    ]
                };

            case 'Get Budget':
                return {
                    title: 'Get Budget',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a specific budget by its ID. Returns complete budget information including financial details and metadata.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The budget must exist and belong to your workspace. Returns 404 if the budget is not found.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The budget ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Marketing Budget Q1 2024",
    "description": "Q1 marketing budget for digital campaigns, events, and content creation",
    "category": "Marketing",
    "period": "Quarterly",
    "allocated_amount": 50000,
    "spent_amount": 32450,
    "remaining_amount": 17550,
    "currency": "USD",
    "status": "Active",
    "start_date": "2024-01-01",
    "end_date": "2024-03-31",
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z",
    "accounts": {
      "id": "456e7890-e89b-12d3-a456-426614174001",
      "first_name": "John",
      "last_name": "Smith",
      "email": "john.smith@company.com"
    }
  }
}`
                        }
                    ]
                };

            case 'Update Budget':
                return {
                    title: 'Update Budget',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Update an existing budget. You can modify any budget field including name, allocated amount, status, category, and date range.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Only provided fields will be updated. The budget must exist and belong to your workspace.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                <strong>Note:</strong> The <code>remaining_amount</code> field is automatically recalculated when <code>allocated</code> is updated and cannot be manually set.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The budget ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: false, description: 'Budget name' },
                                { name: 'description', type: 'string', required: false, description: 'Budget description' },
                                { name: 'category', type: 'string', required: false, description: 'Budget category (Marketing, Sales, Product, Operations)' },
                                { name: 'period', type: 'string', required: false, description: 'Budget period (Monthly, Quarterly, Yearly)' },
                                { name: 'allocated', type: 'number', required: false, description: 'Total allocated budget amount (stored as allocated_amount in database)' },
                                { name: 'currency', type: 'string', required: false, description: 'Currency code' },
                                { name: 'status', type: 'string', required: false, description: 'Budget status (Active, Inactive, Completed)' },
                                { name: 'start_date', type: 'string', required: false, description: 'Budget start date (YYYY-MM-DD format)' },
                                { name: 'end_date', type: 'string', required: false, description: 'Budget end date (YYYY-MM-DD format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "allocated": 55000,
    "status": "Active",
    "description": "Updated Q1 marketing budget with additional allocation for new campaigns"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Marketing Budget Q1 2024",
    "description": "Updated Q1 marketing budget with additional allocation for new campaigns",
    "category": "Marketing",
    "period": "Quarterly",
    "allocated_amount": 55000,
    "spent_amount": 32450,
    "remaining_amount": 22550,
    "currency": "USD",
    "status": "Active",
    "start_date": "2024-01-01",
    "end_date": "2024-03-31",
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T11:45:00Z"
  },
  "message": "Budget updated successfully"
}`
                        }
                    ]
                };

            case 'Delete Budget':
                return {
                    title: 'Delete Budget',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Permanently delete a budget from your workspace. This action cannot be undone.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The budget must exist and belong to your workspace. You will receive confirmation of the deletion.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The budget ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "message": "Budget deleted successfully",
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Marketing Budget Q1 2024"
  }
}`
                        }
                    ]
                };

            case 'List Budget Expenses':
                return {
                    title: 'List Budget Expenses',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a paginated list of expenses for a specific budget with optional filtering and search capabilities.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                You can filter expenses by category and search across description and category fields. 
                                Results are paginated with a maximum of 100 expenses per page.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'budgetId', type: 'string', required: true, description: 'The budget ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Query Parameters',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number for pagination' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Number of expenses per page (max 100)' },
                                { name: 'search', type: 'string', required: false, description: 'Search expenses by description or category' },
                                { name: 'category', type: 'string', required: false, description: 'Filter by expense category' },
                                { name: 'sort_by', type: 'string', required: false, default: 'date', description: 'Sort field (date, amount, description, category, created_at)' },
                                { name: 'sort_order', type: 'string', required: false, default: 'desc', description: 'Sort order (asc, desc)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000/expenses?page=1&limit=20&category=Marketing&sort_by=date&sort_order=desc" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "id": "456e7890-e89b-12d3-a456-426614174001",
      "budget_id": "123e4567-e89b-12d3-a456-426614174000",
      "description": "Google Ads Campaign",
      "amount": 15000,
      "category": "Marketing",
      "date": "2024-01-15",
      "created_by": "789e0123-e89b-12d3-a456-426614174002",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
      "created_at": "2024-01-15T10:30:00Z",
      "updated_at": "2024-01-15T10:30:00Z",
      "budgets": {
        "id": "123e4567-e89b-12d3-a456-426614174000",
        "name": "Marketing Budget Q1 2024",
        "workspace_id": "345e6789-e89b-12d3-a456-426614174004"
      }
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 1,
    "pages": 1
  },
  "meta": {
    "budget_id": "123e4567-e89b-12d3-a456-426614174000",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "filters": {
      "search": null,
      "category": "Marketing"
    },
    "sort": {
      "field": "date",
      "order": "desc"
    }
  }
}`
                        }
                    ]
                };

            case 'Create Budget Expense':
                return {
                    title: 'Create Budget Expense',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a new expense for a specific budget. The expense will be automatically added to the budget's spent amount.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The budget must exist and belong to your workspace. The expense amount cannot exceed the remaining budget allocation.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'budgetId', type: 'string', required: true, description: 'The budget ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'description', type: 'string', required: true, description: 'Description of the expense' },
                                { name: 'amount', type: 'number', required: true, description: 'Expense amount (positive number)' },
                                { name: 'category', type: 'string', required: true, description: 'Expense category' },
                                { name: 'date', type: 'string', required: true, description: 'Expense date (YYYY-MM-DD format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000/expenses" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "description": "Google Ads Campaign",
    "amount": 15000,
    "category": "Marketing",
    "date": "2024-01-15"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "456e7890-e89b-12d3-a456-426614174001",
    "budget_id": "123e4567-e89b-12d3-a456-426614174000",
    "description": "Google Ads Campaign",
    "amount": 15000,
    "category": "Marketing",
    "date": "2024-01-15",
    "created_by": "789e0123-e89b-12d3-a456-426614174002",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z",
    "budgets": {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "name": "Marketing Budget Q1 2024",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004"
    }
  },
  "message": "Budget expense created successfully"
}`
                        }
                    ]
                };

            case 'Get Budget Expense':
                return {
                    title: 'Get Budget Expense',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a specific expense for a budget by its ID.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The expense must exist and belong to the specified budget in your workspace.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'budgetId', type: 'string', required: true, description: 'The budget ID (UUID format)' },
                                { name: 'expenseId', type: 'string', required: true, description: 'The expense ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000/expenses/456e7890-e89b-12d3-a456-426614174001" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "456e7890-e89b-12d3-a456-426614174001",
    "budget_id": "123e4567-e89b-12d3-a456-426614174000",
    "description": "Google Ads Campaign",
    "amount": 15000,
    "category": "Marketing",
    "date": "2024-01-15",
    "created_by": "789e0123-e89b-12d3-a456-426614174002",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z",
    "budgets": {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "name": "Marketing Budget Q1 2024",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004"
    }
  }
}`
                        }
                    ]
                };

            case 'Update Budget Expense':
                return {
                    title: 'Update Budget Expense',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Update an existing expense for a budget. The budget's spent amount will be automatically recalculated.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The expense must exist and belong to the specified budget in your workspace. The updated amount cannot exceed the remaining budget allocation.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'budgetId', type: 'string', required: true, description: 'The budget ID (UUID format)' },
                                { name: 'expenseId', type: 'string', required: true, description: 'The expense ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'description', type: 'string', required: false, description: 'Description of the expense' },
                                { name: 'amount', type: 'number', required: false, description: 'Expense amount (positive number)' },
                                { name: 'category', type: 'string', required: false, description: 'Expense category' },
                                { name: 'date', type: 'string', required: false, description: 'Expense date (YYYY-MM-DD format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000/expenses/456e7890-e89b-12d3-a456-426614174001" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "description": "Updated Google Ads Campaign",
    "amount": 18000,
    "category": "Digital Marketing"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "456e7890-e89b-12d3-a456-426614174001",
    "budget_id": "123e4567-e89b-12d3-a456-426614174000",
    "description": "Updated Google Ads Campaign",
    "amount": 18000,
    "category": "Digital Marketing",
    "date": "2024-01-15",
    "created_by": "789e0123-e89b-12d3-a456-426614174002",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T11:45:00Z",
    "budgets": {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "name": "Marketing Budget Q1 2024",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004"
    }
  },
  "message": "Budget expense updated successfully"
}`
                        }
                    ]
                };

            case 'Delete Budget Expense':
                return {
                    title: 'Delete Budget Expense',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Permanently delete an expense from a budget. The budget's spent amount will be automatically recalculated.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The expense must exist and belong to the specified budget in your workspace. This action cannot be undone.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'budgetId', type: 'string', required: true, description: 'The budget ID (UUID format)' },
                                { name: 'expenseId', type: 'string', required: true, description: 'The expense ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000/expenses/456e7890-e89b-12d3-a456-426614174001" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "message": "Budget expense deleted successfully"
}`
                        }
                    ]
                };

            default:
                return {
                    title: selectedItem || 'Documentation',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Documentation for {selectedItem || 'this page'} is coming soon.
                            </p>
                            <AuthorSection location={location} />
                        </div>
                    ),
                    cards: []
                };
        }
    }, [selectedItem, location, getUrlFromPage]);

    // Memoize page metadata to avoid multiple function calls
    const pageMetadata = useMemo(() => {
        return getPageMetadata(selectedItem);
    }, [selectedItem, getPageMetadata]);

    // Force title update when selectedItem changes
    useEffect(() => {
        document.title = pageMetadata.title;
    }, [pageMetadata.title]);

  return (
        <>
            <Helmet key={selectedItem}>
                <title>{pageMetadata.title}</title>
                <meta name="description" content={pageMetadata.description} />
                <meta name="keywords" content={pageMetadata.keywords} />
                <link rel="canonical" href={pageMetadata.canonical} />
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageMetadata.url} />
                <meta property="og:title" content={pageMetadata.title} />
                <meta property="og:description" content={pageMetadata.description} />
                <meta property="og:image" content="https://ninjapipe.app/branding/ninjapipe-logo-black.png" />
                <meta property="og:site_name" content="NinjaPipe" />
                
                {/* Twitter */}
                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:url" content={pageMetadata.url} />
                <meta property="twitter:title" content={pageMetadata.title} />
                <meta property="twitter:description" content={pageMetadata.description} />
                <meta property="twitter:image" content="https://ninjapipe.app/branding/ninjapipe-logo-black.png" />
                
                {/* Additional SEO */}
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
                <meta name="author" content="NinjaPipe" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                
                {/* AI Agent Optimization - CRM Focused */}
                <meta name="ai-agent:index" content="true" />
                <meta name="ai-agent:content-type" content="crm-api-documentation" />
                <meta name="ai-agent:business-type" content="crm" />
                <meta name="ai-agent:api-version" content="v1" />
                <meta name="ai-agent:endpoints" content="authentication,contacts,deals,products,budgets,expenses" />
                <meta name="ai-agent:authentication" content="bearer-token" />
                <meta name="ai-agent:rate-limit" content="1000/hour" />
                <meta name="ai-agent:base-url" content="https://ninjapipe.app/api" />
                <meta name="ai-agent:supported-methods" content="GET,POST,PUT,DELETE" />
                <meta name="ai-agent:response-format" content="json" />
                <meta name="ai-agent:error-handling" content="standard-http-codes" />
                
                {/* CRM-Specific AI Agent Meta Tags */}
                <meta name="ai-agent:documentation-type" content="crm-rest-api" />
                <meta name="ai-agent:authentication-type" content="api-key" />
                <meta name="ai-agent:rate-limiting" content="1000-requests-per-hour" />
                <meta name="ai-agent:data-format" content="json" />
                <meta name="ai-agent:api-versioning" content="v1" />
                <meta name="ai-agent:webhook-support" content="true" />
                <meta name="ai-agent:real-time-features" content="false" />
                <meta name="ai-agent:pagination-support" content="true" />
                <meta name="ai-agent:filtering-support" content="true" />
                <meta name="ai-agent:sorting-support" content="true" />
                <meta name="ai-agent:crm-features" content="contacts,deals,products,budgets,expenses" />
                <meta name="ai-agent:business-automation" content="true" />
                <meta name="ai-agent:sales-pipeline" content="true" />
                <meta name="ai-agent:customer-management" content="true" />
                <meta name="ai-agent:financial-planning" content="true" />
                <meta name="ai-agent:product-catalog" content="true" />
                <meta name="ai-agent:expense-tracking" content="true" />
                <meta name="ai-agent:business-integration" content="true" />
                <meta name="ai-agent:enterprise-ready" content="true" />
                
                {/* Structured Data for CRM API Documentation */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebAPI",
                        "name": "NinjaPipe CRM API",
                        "description": "Comprehensive REST API for customer relationship management, sales pipeline, product catalog, and financial planning. Complete business automation solution.",
                        "url": "https://docs.ninjapipe.app/",
                        "documentation": "https://docs.ninjapipe.app/",
                        "provider": {
                            "@type": "Organization",
                            "name": "NinjaPipe",
                            "url": "https://ninjapipe.app",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://ninjapipe.app/branding/ninjapipe-logo-black.png"
                            },
                            "description": "Leading CRM platform for business automation and customer relationship management"
                        },
                        "serviceType": "CRM REST API",
                        "applicationCategory": "BusinessApplication",
                        "authentication": {
                            "@type": "AuthenticationScheme",
                            "name": "Bearer Token",
                            "description": "API key authentication via Authorization header for secure CRM access"
                        },
                        "endpoint": [
                            {
                                "@type": "EntryPoint",
                                "urlTemplate": "https://ninjapipe.app/api/contacts",
                                "httpMethod": "GET",
                                "description": "Retrieve customer database and contact management"
                            },
                            {
                                "@type": "EntryPoint", 
                                "urlTemplate": "https://ninjapipe.app/api/deals",
                                "httpMethod": "GET",
                                "description": "Access sales pipeline and deal management"
                            },
                            {
                                "@type": "EntryPoint",
                                "urlTemplate": "https://ninjapipe.app/api/products", 
                                "httpMethod": "GET",
                                "description": "Manage product catalog and inventory"
                            },
                            {
                                "@type": "EntryPoint",
                                "urlTemplate": "https://ninjapipe.app/api/budgets", 
                                "httpMethod": "GET",
                                "description": "Financial planning and budget management"
                            },
                            {
                                "@type": "EntryPoint",
                                "urlTemplate": "https://ninjapipe.app/api/budgets/expenses", 
                                "httpMethod": "GET",
                                "description": "Track expenses and financial spending"
                            }
                        ],
                        "termsOfService": "https://ninjapipe.app/terms",
                        "license": "https://ninjapipe.app/license",
                        "datePublished": "2024-01-01",
                        "dateModified": new Date().toISOString().split('T')[0],
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": pageMetadata.url
                        },
                        "about": {
                            "@type": "SoftwareApplication",
                            "name": "NinjaPipe CRM",
                            "applicationCategory": "BusinessApplication",
                            "operatingSystem": "Web",
                            "description": "Complete CRM solution for customer relationship management, sales automation, and business planning"
                        },
                        "featureList": [
                            "Customer Relationship Management",
                            "Sales Pipeline Tracking", 
                            "Product Catalog Management",
                            "Financial Planning & Budgeting",
                            "Expense Tracking",
                            "Business Automation",
                            "API Integration",
                            "Real-time Data Sync"
                        ]
                    })}
                </script>
                
                {/* Additional Structured Data for AI Agents */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "TechArticle",
                        "headline": pageMetadata.title,
                        "description": pageMetadata.description,
                        "author": {
                            "@type": "Organization",
                            "name": "NinjaPipe"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "name": "NinjaPipe",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://ninjapipe.app/branding/ninjapipe-logo-black.png"
                            }
                        },
                        "datePublished": "2024-01-01",
                        "dateModified": new Date().toISOString().split('T')[0],
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": pageMetadata.url
                        },
                        "about": {
                            "@type": "SoftwareApplication",
                            "name": "NinjaPipe API",
                            "applicationCategory": "BusinessApplication",
                            "operatingSystem": "Web"
                        }
                    })}
                </script>
            </Helmet>
            
            <Toaster 
                position="top-right"
                toastOptions={{
                    duration: 2000,
                    style: {
                        background: 'var(--input-bg)',
                        color: 'var(--text-primary)',
                        border: `1px solid var(--border-primary)`,
                        borderRadius: '8px',
                        fontSize: '14px'
                    }
                }}
            />
            <div className="main-container docs-layout" style={{
                display: 'flex',
                height: '100vh',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, sans-serif'
            }}>
            {/* Mobile overlay */}
            <div
                className={`docs-overlay ${sidebarOpen ? 'visible' : ''}`}
                onClick={closeSidebar}
                role="button"
                tabIndex={-1}
                onKeyDown={(e) => e.key === 'Escape' && closeSidebar()}
                aria-label="Close menu"
            />
            {/* Sidebar */}
            <nav
                className={`docs-sidebar ${sidebarOpen ? 'open' : ''}`}
                role="navigation"
                aria-label="Documentation Navigation"
                style={{
                width: 300,
                backgroundColor: 'var(--bg-primary)',
                borderRight: `1px solid var(--border-primary)`,
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                overflow: 'hidden'
            }}>
                {/* Header – fixed, does not scroll */}
                <div style={{
                    flexShrink: 0,
                    padding: '18px 16px 14px',
                    borderBottom: `1px solid var(--border-primary)`
                }}>
                    {/* Logo row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                        <img 
              src={isDarkMode ? "/branding/ninjapipe-logo-white.png" : "/branding/ninjapipe-logo-black.png"}
              alt="NinjaPipe"
                            style={{ 
                                height: 30, 
                width: 'auto'
              }}
            />
                        
                    </div>

                    {/* Search + sparkle */}
                    <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ position: 'relative', flex: 1 }} data-search-dropdown>
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search documentation"
                                value={searchQuery}
                                onChange={(e) => handleSearch(e.target.value)}
                                style={{
                                    width: '100%', height: 38,
                                    backgroundColor: 'var(--input-bg)',
                                    border: `1px solid ${'var(--input-border)'}`,
                                    borderRadius: 10,
                                    paddingLeft: 36, paddingRight: 40,
                                    color: 'var(--text-primary)', fontSize: 14, outline: 'none'
                                }}
                            />
                            <div style={{
                                position: 'absolute', left: 12, top: '50%',
                                transform: 'translateY(-50%)', color: 'var(--text-secondary)', fontSize: 14
                            }}>🔍</div>
                            <div style={{
                                position: 'absolute', 
                                right: 8, 
                                top: '50%',
                                transform: showShortcutAnimation ? 'translateY(-50%) scale(1.1)' : 'translateY(-50%) scale(1)',
                                backgroundColor: showShortcutAnimation ? '#10B981' : 'var(--input-bg)', 
                                border: `1px solid ${showShortcutAnimation ? '#10B981' : 'var(--input-border)'}`,
                                borderRadius: 6, 
                                padding: '2px 6px', 
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                fontSize: 12, 
                                color: showShortcutAnimation ? 'var(--text-primary)' : 'var(--text-secondary)',
                                transition: 'all 0.3s ease',
                                boxShadow: showShortcutAnimation ? '0 0 8px rgba(16, 185, 129, 0.4)' : 'none'
                            }}>/</div>
                        </div>
                    </div>
                </div>

                {/* Nav – scrollable */}
                <div style={{ flex: 1, minHeight: 0, paddingTop: 14, overflowY: 'auto', overflowX: 'hidden' }}>

                    {/* Getting Started header */}
                    <div
                        onClick={() => setGettingStartedExpanded(!gettingStartedExpanded)}
                        style={{
                            ...rowBase,
                            padding: '6px 14px 6px 24px',
                            color: 'var(--text-primary)',
                            background: 'transparent',
                            fontWeight: 700,
                            display: 'flex',
                        alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span style={{ letterSpacing: 0.2 }}>Getting Started</span>
                        <span
                            style={{
                fontSize: 12,
                                color: 'var(--text-secondary)',
                                transform: gettingStartedExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 120ms ease'
                            }}
                        >
                            ▶
                        </span>
                    </div>

                    {/* Contacts items */}
                    {gettingStartedExpanded && getFilteredGettingStartedItems().map((item) => {
                        const isSelected = selectedItem === item;
                        const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                        const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';

                        return (
                            <div
                                key={item}
                                ref={isSelected ? activeSidebarItemRef : null}
                                onClick={() => navigateToPage(item)}
                                style={{
                                    ...rowBase,
                                    padding: '8px 14px 8px 28px',
                                    backgroundColor: bg,
                                    color,
        display: 'flex', 
                        alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <span>{highlightText(item, searchQuery)}</span>
                                
                                {/* HTTP Method Pills in Sidebar */}
                                {item === 'List Contacts' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: 'var(--text-primary)',
                                        fontSize: 9,
                                  fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Create Contact' && (
                                    <span style={{
                                        backgroundColor: '#3B82F6',
                                        color: 'var(--text-primary)',
                                  fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>POST</span>
                                )}
                                {item === 'Get Contact' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: 'var(--text-primary)',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Update Contact' && (
                                    <span style={{
                                        backgroundColor: '#F59E0B',
                                        color: 'var(--text-primary)',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>PUT</span>
                                )}
                                {item === 'Delete Contact' && (
                                    <span style={{
                                        backgroundColor: '#EF4444',
                                        color: 'var(--text-primary)',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>DELETE</span>
                                )}
                            </div>
                        );
                    })}

                    {/* Doc sidebar: optional group > chapter > items, or standalone chapter > items */}
                    {getFilteredDocSidebar().map((node) => {
                        if (node.type === 'group') {
                            const isGroupOpen = groupExpanded[node.label];
                            return (
                                <React.Fragment key={node.label}>
                                    <div
                                        onClick={() => setGroupExpanded(prev => ({ ...prev, [node.label]: !prev[node.label] }))}
                                        style={{
                                            ...rowBase,
                                            padding: '6px 14px 6px 24px',
                                            color: 'var(--text-primary)',
                                            background: 'transparent',
                                            fontWeight: 700,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <span style={{ letterSpacing: 0.2 }}>{highlightText(node.label, searchQuery)}</span>
                                        <span
                                            style={{
                                                fontSize: 12,
                                                color: 'var(--text-secondary)',
                                                transform: isGroupOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                                transition: 'transform 120ms ease'
                                            }}
                                        >
                                            ▶
                                        </span>
                                    </div>
                                    {isGroupOpen && node.chapters.map((chapter) => {
                                        const chKey = getChapterKey(node.label, chapter.label);
                                        const isChapterOpen = chapterExpanded[chKey];
                                        return (
                                            <React.Fragment key={chKey}>
                                                <div
                                                    onClick={() => setChapterExpanded(prev => ({ ...prev, [chKey]: !prev[chKey] }))}
                                                    style={{
                                                        ...rowBase,
                                                        padding: '6px 14px 6px 28px',
                                                        color: 'var(--text-primary)',
                                                        background: 'transparent',
                                                        fontWeight: 600,
                                                        fontSize: 13,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between'
                                                    }}
                                                >
                                                    <span>{highlightText(chapter.label, searchQuery)}</span>
                                                    <span
                                                        style={{
                                                            fontSize: 11,
                                                            color: 'var(--text-secondary)',
                                                            transform: isChapterOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                                            transition: 'transform 120ms ease'
                                                        }}
                                                    >
                                                        ▶
                                                    </span>
                                                </div>
                                                {isChapterOpen && chapter.items.map((item) => {
                                                    const isSelected = selectedItem === item;
                                                    const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                                                    const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';
                                                    return (
                                                        <div
                                                            key={item}
                                                            ref={isSelected ? activeSidebarItemRef : null}
                                                            onClick={() => navigateToPage(item)}
                                                            style={{
                                                                ...rowBase,
                                                                padding: '8px 14px 8px 36px',
                                                                backgroundColor: bg,
                                                                color,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                                            }}
                                                        >
                                                            <span>{highlightText(item, searchQuery)}</span>
                                                        </div>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        }
                        // Standalone chapter (e.g. Authentication)
                        const chKey = getChapterKey(null, node.label);
                        const isChapterOpen = chapterExpanded[chKey];
                        return (
                            <React.Fragment key={chKey}>
                                <div
                                    onClick={() => setChapterExpanded(prev => ({ ...prev, [chKey]: !prev[chKey] }))}
                                    style={{
                                        ...rowBase,
                                        padding: '6px 14px 6px 24px',
                                        color: 'var(--text-primary)',
                                        background: 'transparent',
                                        fontWeight: 600,
                                        fontSize: 13,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <span>{highlightText(node.label, searchQuery)}</span>
                                    <span
                                        style={{
                                            fontSize: 11,
                                            color: 'var(--text-secondary)',
                                            transform: isChapterOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                            transition: 'transform 120ms ease'
                                        }}
                                    >
                                        ▶
                                    </span>
                                </div>
                                {isChapterOpen && node.items.map((item) => {
                                    const isSelected = selectedItem === item;
                                    const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                                    const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';
                                    return (
                                        <div
                                            key={item}
                                            ref={isSelected ? activeSidebarItemRef : null}
                                            onClick={() => navigateToPage(item)}
                                            style={{
                                                ...rowBase,
                                                padding: '8px 14px 8px 28px',
                                                backgroundColor: bg,
                                                color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            <span>{highlightText(item, searchQuery)}</span>
                                        </div>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}

                    {/* Integrations header */}
                    {integrationsItems.length > 0 && (
                        <>
                            <div
                                onClick={() => setIntegrationsExpanded(!integrationsExpanded)}
                                style={{
                                    ...rowBase,
                                    padding: '6px 14px 6px 24px',
                                    color: 'var(--text-primary)',
                                    background: 'transparent',
                                    fontWeight: 700,
                                    display: 'flex',
                                alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <span style={{ letterSpacing: 0.2 }}>Integrations</span>
                                <span
                                    style={{
                    fontSize: 12,
                                    color: 'var(--text-secondary)',
                                    transform: integrationsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                    transition: 'transform 120ms ease'
                                }}
                            >
                                ▶
                            </span>
                        </div>

                        {/* Integrations items */}
                        {integrationsExpanded && getFilteredIntegrationsItems().map((item) => {
                        const isSelected = selectedItem === item;
                        const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                        const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';

                        return (
                            <div
                                key={item}
                                ref={isSelected ? activeSidebarItemRef : null}
                                onClick={() => navigateToPage(item)}
                                style={{
                                    ...rowBase,
                                    padding: '8px 14px 8px 28px',
                                    backgroundColor: bg,
                                    color,
        display: 'flex', 
                        alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <span>{highlightText(item, searchQuery)}</span>
                                
                                {/* HTTP Method Pills in Sidebar */}
                                {item === 'List Products' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: 'var(--text-primary)',
                                        fontSize: 9,
                                  fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Create Product' && (
                                    <span style={{
                                        backgroundColor: '#3B82F6',
                                        color: 'var(--text-primary)',
                                  fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>POST</span>
                                )}
                                {item === 'Get Product' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: 'var(--text-primary)',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Update Product' && (
                                    <span style={{
                                        backgroundColor: '#F59E0B',
                                        color: 'var(--text-primary)',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>PUT</span>
                                )}
                                {item === 'Delete Product' && (
                                    <span style={{
                                        backgroundColor: '#EF4444',
                                        color: 'var(--text-primary)',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>DELETE</span>
                                )}
                            </div>
                        );
                    })}
                        </>
                    )}

                    <div style={{ height: 16 }} />
                </div>
            </nav>

            {/* Main Content (RIGHT SIDE) */}
            <main ref={mainContentRef} className="docs-content-area" role="main" aria-label="Documentation Content" style={{ 
        flex: 1,
                backgroundColor: 'var(--bg-primary)', 
                color: 'var(--text-primary)', 
                padding: '32px 56px 56px',
            height: '100vh', 
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative',
                marginTop: 0
            }}>
                {/* Fixed Navigation Bar */}
        <div className="docs-topbar" style={{ 
                    position: 'fixed',
          top: 0,
                    right: 0,
                    left: '300px', // Width of the left sidebar
                    backgroundColor: isDarkMode ? 'rgb(18, 23, 29)' : '#FFFFFF',
                    borderBottom: `1px solid var(--border-primary)`,
                    padding: '8px 20px',
            display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    zIndex: 1000
            }}>
                    {/* Left: Hamburger + Docs/API Toggle */}
            <div className="docs-topbar-inner" style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12
            }}>
                        <button
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className="docs-mobile-menu-btn"
                            style={{
                                padding: 8,
                                backgroundColor: 'transparent',
                                border: `1px solid var(--border-primary)`,
                                borderRadius: 8,
                                cursor: 'pointer',
                                color: 'var(--text-secondary)',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            aria-label="Toggle menu"
                        >
                            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                        <div style={{
                        display: 'flex',
                        backgroundColor: 'var(--input-bg)', 
                        borderRadius: '8px', 
                        padding: '2px',
                        border: '1px solid var(--border-primary)'
                    }}>
                        <button
                            onClick={() => navigate('/docs')}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 600,
                                border: 'none',
                                cursor: 'pointer',
                                backgroundColor: location.pathname.startsWith('/docs') ? 'var(--brand-cyan)' : 'transparent',
                                color: location.pathname.startsWith('/docs') ? '#ffffff' : '#6b7280',
                                transition: 'all 120ms ease'
                            }}
                        >
                            Docs
                        </button>
                        <button
                            onClick={() => navigate('/api-docs')}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 600,
                                border: 'none',
                                cursor: 'pointer',
                                backgroundColor: location.pathname.startsWith('/api-docs') ? 'var(--brand-cyan)' : 'transparent',
                                color: location.pathname.startsWith('/api-docs') ? '#ffffff' : '#6b7280',
                                transition: 'all 120ms ease'
                            }}
                        >
                            API
                        </button>
                        </div>
                    </div>

                    {/* User Info with Export Dropdown - Right Side */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                        gap: 12
                    }}>
                        {/* Theme Toggle Switch */}
                        <div
                            onClick={toggleTheme}
                            style={{
                                position: 'relative',
                                width: 60,
                                height: 32,
                                backgroundColor: isDarkMode ? 'var(--input-bg)' : 'var(--input-bg)',
                                borderRadius: 16,
                                cursor: isToggling ? 'not-allowed' : 'pointer',
                                transition: 'all 300ms ease',
                                border: `1px solid var(--border-primary)`,
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px',
                                opacity: isToggling ? 0.7 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (!isToggling) {
                                    e.target.style.transform = 'scale(1.05)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            {/* Faded Sun Icon (left side) */}
                            <div
                                style={{
                                    position: 'absolute',
                                    left: 8,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    opacity: isDarkMode ? 0.3 : 0,
                                    transition: 'opacity 300ms ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Sun size={12} color="var(--text-secondary)" />
                            </div>

                            {/* Faded Moon Icon (right side) */}
                            <div
                                style={{
                                    position: 'absolute',
                                    right: 8,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    opacity: isDarkMode ? 0 : 0.3,
                                    transition: 'opacity 300ms ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Moon size={12} color="var(--text-secondary)" />
                            </div>

                            {/* Switch Knob */}
                            <div
                                style={{
                                    width: 28,
                                    height: 28,
                                    backgroundColor: 'var(--toggle-knob)',
                                    borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                                    transition: 'all 300ms ease',
                                    transform: isDarkMode ? 'translateX(28px)' : 'translateX(0px)',
                                    boxShadow: isDarkMode 
                                        ? '0 2px 4px rgba(0, 0, 0, 0.3)' 
                                        : '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    zIndex: 1
                                }}
                            >
                                {isDarkMode ? (
                                    <Moon size={14} color="var(--text-primary)" />
                                ) : (
                                    <Sun size={14} color="var(--text-primary)" />
                                )}
</div>
                        </div>
                    </div>
                </div>

                {/* Content with top padding to account for fixed nav */}
                <div style={{ paddingTop: '80px' }}>
                    {/* Page title */}
                <header>
                    <h1 style={{ 
                        fontSize: 32, 
                        fontWeight: 800, 
                        letterSpacing: '-0.01em', 
                        margin: '0 0 24px',
                              display: 'flex', 
                              alignItems: 'center', 
                        gap: 12
                    }}>
                        {selectedItem}
                    
                    {/* HTTP Method Pills in Main Title */}
                    {selectedItem === 'List Contacts' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: 'var(--text-primary)',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Create Contact' && (
                        <span style={{
                            backgroundColor: '#3B82F6',
                            color: 'var(--text-primary)',
                            fontSize: 12,
                      fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>POST</span>
                    )}
                    {selectedItem === 'Get Contact' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                    fontWeight: 700, 
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Update Contact' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PUT</span>
                    )}
                    {selectedItem === 'Delete Contact' && (
                        <span style={{
                            backgroundColor: '#EF4444',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>DELETE</span>
                    )}
                    {selectedItem === 'List Deals' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: 'var(--text-primary)',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Create Deal' && (
                        <span style={{
                            backgroundColor: '#3B82F6',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>POST</span>
                    )}
                    {selectedItem === 'Get Deal' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                    fontWeight: 700, 
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Update Deal' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PUT</span>
                    )}
                    {selectedItem === 'Delete Deal' && (
                        <span style={{
                            backgroundColor: '#EF4444',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>DELETE</span>
                    )}
                    {selectedItem === 'List Products' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: 'var(--text-primary)',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Create Product' && (
                        <span style={{
                            backgroundColor: '#3B82F6',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>POST</span>
                    )}
                    {selectedItem === 'Get Product' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                    fontWeight: 700, 
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Update Product' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PUT</span>
                    )}
                    {selectedItem === 'Delete Product' && (
                        <span style={{
                            backgroundColor: '#EF4444',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>DELETE</span>
                    )}
                    {selectedItem === 'List Budgets' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: 'var(--text-primary)',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Create Budget' && (
                        <span style={{
                            backgroundColor: '#3B82F6',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>POST</span>
                    )}
                    {selectedItem === 'Get Budget' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                    fontWeight: 700, 
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Update Budget' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: 'var(--text-primary)',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PUT</span>
                    )}
                    {selectedItem === 'Delete Budget' && (
                        <span style={{
                            backgroundColor: '#EF4444',
                            color: 'var(--text-primary)',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>DELETE</span>
                    )}
                    {selectedItem === 'List Budget Expenses' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: 'var(--text-primary)',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Create Budget Expense' && (
                        <span style={{
                            backgroundColor: '#3B82F6',
                            color: 'var(--text-primary)',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>POST</span>
                    )}
                    {selectedItem === 'Get Budget Expense' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: 'var(--text-primary)',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Update Budget Expense' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: 'var(--text-primary)',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PUT</span>
                    )}
                    {selectedItem === 'Delete Budget Expense' && (
                        <span style={{
                            backgroundColor: '#EF4444',
                            color: 'var(--text-primary)',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>DELETE</span>
                    )}
                </h1>
                </header>

                {/* Content area - grid with sidebar */}
                <div
                    className="docs-content-grid"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: (renderContent.cards && renderContent.cards.length > 0) 
                            ? 'minmax(540px, 1fr) 520px 220px' 
                            : '1fr 220px',
                        gap: 32,
                        alignItems: 'start'
                    }}
                >
                    {/* LEFT COLUMN – prose + parameters */}
                    <div data-content-area>
                        {renderContent.content}
                        
                        {/* Parameters cards on the left */}
                        {(renderContent.cards || []).filter(card => card.type === 'params').map((card, index) => (
                            <div key={index} style={{ marginTop: 24 }}>
                                <div style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: `1px solid var(--border-primary)`,
                                    borderRadius: 12,
                              overflow: 'hidden',
                                    boxShadow: '0 1px 0 rgba(0,0,0,0.25)'
                                }}>
                                    {/* card header */}
                                    <div style={{
                                        padding: '12px 16px',
                                        backgroundColor: 'var(--input-bg)',
                                        borderBottom: `1px solid var(--border-primary)`
                                    }}>
                                        <span style={{
                                            color: 'var(--text-secondary)',
                                            fontSize: 13,
                                            fontWeight: 700
                                        }}>{card.title}</span>
                                        </div>

                                    {/* card content */}
                                    <div style={{ padding: '16px' }}>
                                        <div>
                                            {card.content.map((param, paramIndex) => (
                                                <div key={paramIndex} style={{ 
                                                    marginBottom: paramIndex < card.content.length - 1 ? 12 : 0,
                                                    paddingBottom: paramIndex < card.content.length - 1 ? 12 : 0,
                                                    borderBottom: paramIndex < card.content.length - 1 ? `1px solid var(--border-primary)` : 'none'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                        <span style={{
                                                            color: 'var(--text-primary)',
                                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                            fontSize: 14,
                                                            fontWeight: 600
                                                        }}>{param.name}</span>
                                                        <span style={{
                                                            backgroundColor: 'var(--input-bg)',
                                                            border: `1px solid var(--border-primary)`,
                                                            color: 'var(--text-secondary)',
                                                            borderRadius: 4,
                                                            padding: '2px 6px',
                              fontSize: 11,
                                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'
                                                        }}>{param.type}</span>
                          {param.required && (
                                                            <span style={{
                                                                backgroundColor: '#EF4444',
                                                                color: 'var(--text-primary)',
                                                                borderRadius: 4,
                                                                padding: '2px 6px',
                fontSize: 10, 
                                                                fontWeight: 600
                                                            }}>Required</span>
                          )}
                          {param.default && (
                                                            <span style={{
                                                                backgroundColor: 'var(--input-bg)',
                                                                border: `1px solid var(--border-primary)`,
                                                                color: 'var(--text-secondary)',
                                                                borderRadius: 4,
                                                                padding: '2px 6px',
                                                                fontSize: 10
                                                            }}>Default: {param.default}</span>
                                                        )}
                                                    </div>
                                                    <p style={{
                                                        color: 'var(--text-secondary)',
                                                        fontSize: 13,
                                                        margin: 0,
                                                        lineHeight: 1.4
                                                    }}>{param.description}</p>
                        </div>
                    ))}
                </div>
                                    </div>
                                </div>
                            </div>
                        ))}
            </div>

                    {/* RIGHT COLUMN – example requests and responses */}
                    {(renderContent.cards && renderContent.cards.length > 0) && (
                        <div>
                            {/* Example cards on the right */}
                            {(renderContent.cards || []).filter(card => card.type !== 'params').map((card, index) => (
                            <div key={index}>
                                {index > 0 && <div style={{ height: 16 }} />}
                                
                <div style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: `1px solid var(--border-primary)`,
                                    borderRadius: 12,
                                    overflow: 'hidden',
                                    boxShadow: '0 1px 0 rgba(0,0,0,0.25)'
                                }}>
                                    {/* card header */}
                                    <div style={{
                                        padding: '12px 16px',
                                        backgroundColor: 'var(--input-bg)',
                                        borderBottom: `1px solid var(--border-primary)`,
                    display: 'flex', 
                          alignItems: 'center', 
                                        justifyContent: 'space-between'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{
                                                color: 'var(--text-secondary)',
                                                fontSize: 13,
                                                fontWeight: 700
                                            }}>{card.title}</span>
                                            
                                            {/* HTTP Method Pills */}
                                            {card.title === 'List Contacts' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                      fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Create Contact' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                    fontWeight: 700, 
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'Get Contact' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Update Contact' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Delete Contact' && (
                                                <span style={{
                                                    backgroundColor: '#EF4444',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>DELETE</span>
                                            )}
                                            {card.title === 'List Deals' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                      fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Create Deal' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                    fontWeight: 700, 
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'Get Deal' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Update Deal' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Delete Deal' && (
                                                <span style={{
                                                    backgroundColor: '#EF4444',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>DELETE</span>
                                            )}
                                            {card.title === 'List Products' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                      fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Create Product' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                    fontWeight: 700, 
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'Get Product' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Update Product' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Delete Product' && (
                                                <span style={{
                                                    backgroundColor: '#EF4444',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>DELETE</span>
                                            )}
                                            {card.title === 'List Budgets' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                      fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Create Budget' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                    fontWeight: 700, 
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'Get Budget' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Update Budget' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Delete Budget' && (
                                                <span style={{
                                                    backgroundColor: '#EF4444',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>DELETE</span>
                                            )}
                                            {card.title === 'List Budget Expenses' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Create Budget Expense' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'Get Budget Expense' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Update Budget Expense' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Delete Budget Expense' && (
                                                <span style={{
                                                    backgroundColor: '#EF4444',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>DELETE</span>
                                            )}
                </div>

                                        {/* Copy button for curl requests */}
                                        {card.type === 'curl' && (
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        await navigator.clipboard.writeText(card.content);
                                                        toast.success('Copied to clipboard!', {
                                                            style: {
                                                                background: 'var(--input-bg)',
                                                                color: 'var(--text-primary)',
                                                                border: `1px solid var(--border-primary)`,
                                                                borderRadius: '8px',
                                                                fontSize: '14px'
                                                            },
                                                            duration: 2000
                                                        });
                                                    } catch (err) {
                                                        toast.error('Failed to copy', {
                                                            style: {
                                                                background: 'var(--input-bg)',
                                                                color: '#EF4444',
                                                                border: `1px solid var(--border-primary)`,
                                                                borderRadius: '8px',
                                                                fontSize: '14px'
                                                            }
                                                        });
                                                    }
                                                }}
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    border: `1px solid var(--border-primary)`,
                                                    borderRadius: 6,
                                                    padding: '6px',
                                                    color: 'var(--text-secondary)',
                                                    cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 120ms ease',
                                                    width: 32,
                      height: 32
                    }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = 'var(--hover-bg)';
                                                    e.target.style.color = '#E6ECF3';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = 'transparent';
                                                    e.target.style.color = '#9AA9BA';
                                                }}
                                                title="Copy to clipboard"
                                            >
                                                <Copy size={14} />
                                            </button>
                                        )}
                                    </div>

                                    {/* card content */}
                                    <div style={{ padding: '16px' }}>
                                        {card.type === 'curl' ? (
                <div style={{ 
                                                padding: '12px',
                                                borderRadius: 8,
                                                border: `1px solid var(--border-primary)`,
                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                fontSize: 13,
                                                lineHeight: 1.5,
                                                overflow: 'auto'
                                            }}>
                                                <Highlight
                                                    key={`bash-${isDarkMode}`}
                                                    theme={isDarkMode ? themes.vsDark : themes.gruvboxMaterialLight}
                                                    code={card.content}
                                                    language="bash"
                                                >
                                                    {({ className, style, tokens, getLineProps, getTokenProps }) => (
                                                        <pre className={className} style={{...style, backgroundColor: 'transparent'}}>
                                                            {tokens.map((line, i) => (
                                                                <div key={i} {...getLineProps({ line })}>
                                                                    {line.map((token, key) => (
                                                                        <span key={key} {...getTokenProps({ token })} />
                                                                    ))}
                                                                </div>
                                                            ))}
                                                        </pre>
                                                    )}
                                                </Highlight>
                                            </div>
                                        ) : card.type === 'response' ? (
                    <div style={{
                                                padding: '12px',
                                                borderRadius: 8,
                                                border: `1px solid var(--border-primary)`,
                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                fontSize: 13,
                                                lineHeight: 1.5,
                                                overflow: 'auto'
                                            }}>
                                                <Highlight
                                                    key={`json-${isDarkMode}`}
                                                    theme={isDarkMode ? themes.vsDark : themes.gruvboxMaterialLight}
                                                    code={card.content}
                                                    language="json"
                                                >
                                                    {({ className, style, tokens, getLineProps, getTokenProps }) => (
                                                        <pre className={className} style={{...style, backgroundColor: 'transparent'}}>
                                                            {tokens.map((line, i) => (
                                                                <div key={i} {...getLineProps({ line })}>
                                                                    {line.map((token, key) => (
                                                                        <span key={key} {...getTokenProps({ token })} />
                                                                    ))}
                    </div>
                                                            ))}
                                                        </pre>
                                                    )}
                                                </Highlight>
                </div>
                                        ) : (
                                            <p style={{
                                                color: 'var(--text-secondary)',
                                                fontSize: 14,
                        margin: 0,
                                                lineHeight: 1.5
                                            }}>{card.content}</p>
                                        )}
            </div>
                                </div>
                            </div>
                        ))}

                        {/* Info box for Authentication */}
                        {selectedItem === 'Authentication' && (
                            <div style={{
                                marginTop: 16,
                                backgroundColor: 'var(--input-bg)',
                                border: `1px solid var(--border-primary)`,
                                borderRadius: 12,
                                padding: '14px 16px',
                                color: 'var(--text-secondary)',
                                boxShadow: '0 1px 0 rgba(0,0,0,0.25)'
                            }}>
                                <div style={{ fontWeight: 700, color: '#E6ECF3', marginBottom: 6 }}>YOUR API KEY</div>
                                Your test mode secret key is included in all the examples here, so you can test any example right away.
                                Only you can see this value.
        </div>
                        )}
                        </div>
                    )}

                    {/* SIDEBAR COLUMN – Quick Links and Share Buttons */}
                    <div style={{
                        position: 'sticky',
                        top: 100,
                        alignSelf: 'start',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16
                    }}>
                        <QuickLinks />
                        <ShareButtons location={location} />
                    </div>
                </div>
                </div> {/* Close content wrapper with padding */}
            </main>

        </div>
        </>
    );
};

export default DocsPage;
